import { useEffect, useRef, useState } from "react";
import { Routes, Route, useNavigate, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  Factory,
  Package,
  PackageOpen,
  FileText,
  LogOut,
  ChevronLeft,
  CheckCircle2,
  Loader2,
  Keyboard,
  Ban,
  AlertTriangle,
  Eye,
  EyeOff,
  ListChecks,
  Clock,
  Activity,
  RefreshCw,
  Boxes,
  Search,
  ArrowUpCircle,
  ArrowDownCircle,
  Sliders,
  AlertCircle,
  CloudOff,
  ArrowLeftRight,
  ChevronRight,
  ScanLine,
  Plus,
  Trash2,
  XCircle,
  Volume2,
  VolumeX,
  FileSpreadsheet,
  Download,
  Lock,
  Unlock,
  Undo2,
  BarChart3,
  Camera,
  MapPin,
  Maximize2,
  StickyNote,
  X,
} from "lucide-react";
import { api, mobileAuth, invCache, extractProductCode, isPalletId } from "./mobileApi";
import { fmtDateTime } from "../lib/datetime";
import ZoomableImage from "./ZoomableImage";
import Scanner from "./Scanner";
import { setPwaIdentity } from "../pwaIdentity";
import "./mobile.css";

const DEVICE_ID = (() => {
  const k = "mobile_device_id";
  let v = localStorage.getItem(k);
  if (!v) {
    v = "PWA-" + Math.random().toString(36).slice(2, 10);
    localStorage.setItem(k, v);
  }
  return v;
})();

export default function MobileApp() {
  const [user, setUser] = useState(mobileAuth.getUser());
  const [bootChecked, setBootChecked] = useState(false);

  useEffect(() => {
    // Restore the shop-floor PWA identity (manifest + iOS apple-touch-icon/title).
    setPwaIdentity({
      manifest: "/manifest.webmanifest",
      title: "Manik Shop",
      themeColor: "#ea580c",
      appleIcon: "/shop-icon-180.png",
    });
  }, []);

  useEffect(() => {
    // Boot check: if we have a token, verify it; otherwise clear
    (async () => {
      const tok = mobileAuth.getToken();
      if (!tok) {
        setBootChecked(true);
        return;
      }
      try {
        const me = await api.me();
        mobileAuth.setAuth(tok, me);
        setUser(me);
      } catch {
        mobileAuth.clear();
        setUser(null);
      } finally {
        setBootChecked(true);
      }
    })();
  }, []);

  if (!bootChecked) {
    return <div className="m-app"><div className="m-screen"><Loader2 className="animate-spin mx-auto mt-12" /></div></div>;
  }

  return (
    <div className="m-app" data-testid="mobile-app">
      <Toaster richColors position="bottom-center" />
      <Routes>
        <Route path="/" element={user ? <Home user={user} setUser={setUser} /> : <Navigate to="login" />} />
        <Route path="login" element={<Login onLoggedIn={(u) => setUser(u)} />} />
        <Route path="pin-setup" element={user ? <PinSetup /> : <Navigate to="login" />} />
        <Route path="produce" element={user ? <Produce user={user} /> : <Navigate to="login" />} />
        <Route path="update" element={user ? <UpdatePallet user={user} /> : <Navigate to="login" />} />
        <Route path="update/:palletId" element={user ? <UpdatePallet user={user} /> : <Navigate to="login" />} />
        <Route path="my" element={user ? <MyPallets user={user} /> : <Navigate to="login" />} />
        <Route path="pack" element={user ? <Pack user={user} /> : <Navigate to="login" />} />
        <Route path="live" element={user ? <LiveProduction user={user} /> : <Navigate to="login" />} />
        <Route path="inventory" element={user ? <InventoryList user={user} /> : <Navigate to="login" />} />
        <Route path="inventory/product/:code" element={user ? <ProductDetail user={user} /> : <Navigate to="login" />} />
        <Route path="stock-count" element={user ? <StockCountMobile user={user} /> : <Navigate to="login" />} />
        <Route path="*" element={<Navigate to="/mobile" />} />
      </Routes>
    </div>
  );
}

// ============================== Login (password OR PIN) ==============================

function Login({ onLoggedIn }) {
  const navigate = useNavigate();
  const remembered = mobileAuth.getRememberedEmail();
  const [mode, setMode] = useState(remembered ? "pin" : "password");
  const [email, setEmail] = useState(remembered || "");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setErr(null);
    try {
      const data = mode === "pin"
        ? await api.loginPin(email.trim(), pin)
        : await api.loginPassword(email.trim(), password);
      mobileAuth.setAuth(data.access_token, data.user);
      onLoggedIn(data.user);
      // If logging in via password and no PIN set, send to PIN setup
      if (mode === "password") {
        try {
          const status = await api.pinStatus();
          if (!status.pin_set) {
            navigate("/mobile/pin-setup");
            return;
          }
        } catch { /* ignore */ }
      }
      navigate("/mobile");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="m-chrome">
        <div>
          <div className="m-chrome-title">Manik Shop Floor</div>
          <div className="m-chrome-sub">Scan-driven production</div>
        </div>
      </div>
      <div className="m-screen">
        <h2 style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 22, marginBottom: 4 }}>
          {mode === "pin" ? "Quick login" : "Sign in"}
        </h2>
        <p style={{ color: "#a8a29e", fontSize: 13, marginBottom: 16 }}>
          {mode === "pin"
            ? "Enter your 4-digit PIN to start a session"
            : "Use your ERP email and password the first time"}
        </p>

        <form onSubmit={submit}>
          <input
            className="m-input"
            type="email"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="mobile-login-email"
            style={{ marginBottom: 12 }}
            disabled={mode === "pin" && !!remembered}
          />
          {mode === "password" ? (
            <input
              className="m-input"
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="mobile-login-password"
              style={{ marginBottom: 12 }}
            />
          ) : (
            <input
              className="m-input m-pin-input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder="• • • •"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              maxLength={8}
              required
              data-testid="mobile-login-pin"
              style={{ marginBottom: 12 }}
            />
          )}

          {err && <div className="m-error-banner" data-testid="mobile-login-error">{err}</div>}

          <button type="submit" className="m-btn" disabled={busy} data-testid="mobile-login-submit">
            {busy ? "Signing in…" : mode === "pin" ? "Unlock" : "Sign in"}
          </button>
        </form>

        <div style={{ marginTop: 16, textAlign: "center" }}>
          {mode === "pin" ? (
            <button
              className="m-link-btn"
              onClick={() => { setMode("password"); setErr(null); }}
              data-testid="mobile-switch-password"
            >
              Use email + password instead
            </button>
          ) : (
            remembered && (
              <button
                className="m-link-btn"
                onClick={() => { setMode("pin"); setErr(null); }}
                data-testid="mobile-switch-pin"
              >
                Use PIN instead
              </button>
            )
          )}
        </div>
      </div>
    </>
  );
}

// ============================== PIN Setup ==============================

function PinSetup() {
  const navigate = useNavigate();
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (pin1.length < 4 || pin1.length > 8) {
      setErr("PIN must be 4-8 digits");
      return;
    }
    if (pin1 !== pin2) {
      setErr("PINs do not match");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.setPin(pin1);
      toast.success("PIN saved — next login can be a single tap");
      navigate("/mobile");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not save PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="m-chrome">
        <div>
          <div className="m-chrome-title">Set a PIN</div>
          <div className="m-chrome-sub">Quick re-login on this device</div>
        </div>
      </div>
      <div className="m-screen">
        <p style={{ color: "#a8a29e", fontSize: 13, marginBottom: 16 }}>
          Pick a 4-digit PIN. You'll use this to unlock the app at shift changeover instead of typing your password.
        </p>
        <form onSubmit={submit}>
          <input
            className="m-input m-pin-input"
            type="password"
            inputMode="numeric"
            placeholder="New PIN"
            value={pin1}
            onChange={(e) => setPin1(e.target.value.replace(/\D/g, "").slice(0, 8))}
            maxLength={8}
            data-testid="pin-setup-1"
            style={{ marginBottom: 12 }}
          />
          <input
            className="m-input m-pin-input"
            type="password"
            inputMode="numeric"
            placeholder="Confirm PIN"
            value={pin2}
            onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 8))}
            maxLength={8}
            data-testid="pin-setup-2"
            style={{ marginBottom: 12 }}
          />
          {err && <div className="m-error-banner">{err}</div>}
          <button type="submit" className="m-btn" disabled={busy} data-testid="pin-setup-submit">
            {busy ? "Saving…" : "Save PIN"}
          </button>
          <button
            type="button"
            className="m-btn m-btn--ghost"
            style={{ marginTop: 8 }}
            onClick={() => navigate("/mobile")}
            data-testid="pin-setup-skip"
          >
            Skip for now
          </button>
        </form>
      </div>
    </>
  );
}

// ============================== Home (4 tiles, session-less) ==============================

function Home({ user, setUser }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null); // { assigned, produced, packed, voided, pieces }
  const [perms, setPerms] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.permissions();
        setPerms(r?.effective_permissions || []);
      } catch {
        setPerms([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const list = await api.myRecent({ limit: 200 });
        const todayStr = new Date().toISOString().slice(0, 10);
        const today = (list || []).filter((p) => (p.paired_at || "").startsWith(todayStr));
        const s = { assigned: 0, produced: 0, packed: 0, voided: 0, pieces: 0 };
        today.forEach((p) => {
          if (p.state === "ASSIGNED") s.assigned += 1;
          else if (p.state === "PRODUCED") {
            s.produced += 1;
            s.pieces += Number(p.actual_qty) || 0;
          } else if (p.state === "PACKED") {
            s.packed += 1;
            s.pieces += Number(p.actual_qty) || 0;
          } else if (p.state === "VOIDED") s.voided += 1;
        });
        setStats(s);
      } catch {
        setStats({ assigned: 0, produced: 0, packed: 0, voided: 0, pieces: 0 });
      }
    })();
  }, []);

  const signOut = () => {
    mobileAuth.clear();
    setUser(null);
    navigate("/mobile/login");
  };

  return (
    <>
      <div className="m-chrome">
        <div style={{ flex: 1 }}>
          <div className="m-chrome-title">{user?.name || user?.email}</div>
          <div className="m-chrome-sub">device {DEVICE_ID}</div>
        </div>
        <button className="m-back-btn" onClick={signOut} title="Sign out" data-testid="mobile-signout">
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      <div className="m-screen">
        <button
          onClick={() => navigate("/mobile/my")}
          className="m-stats-strip"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 6,
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 14,
            padding: "12px 10px",
            marginBottom: 14,
            cursor: "pointer",
            textAlign: "center",
            width: "100%",
          }}
          data-testid="home-stats-strip"
        >
          <Stat label="Assigned" value={stats?.assigned} color="#fbbf24" testId="stat-assigned" />
          <Stat label="Produced" value={stats?.produced} color="#34d399" testId="stat-produced" />
          <Stat label="Packed" value={stats?.packed} color="#60a5fa" testId="stat-packed" />
          <Stat label="Voided" value={stats?.voided} color="#f87171" testId="stat-voided" />
          <Stat label="Pieces" value={stats?.pieces} color="#fdba74" testId="stat-pieces" />
        </button>

        <div className="m-tile-grid">
          <Tile
            icon={<Factory className="w-5 h-5" />}
            label="Produce"
            hint="Scan & pair new pallets"
            onClick={() => navigate("/mobile/produce")}
            testId="tile-produce"
          />
          <Tile
            icon={<CheckCircle2 className="w-5 h-5" />}
            label="Update Pallet"
            hint="Scan to mark Produced / Void"
            onClick={() => navigate("/mobile/update")}
            testId="tile-update"
          />
          <Tile
            icon={<ListChecks className="w-5 h-5" />}
            label="My Pallets"
            hint="Recent pallets you paired"
            onClick={() => navigate("/mobile/my")}
            testId="tile-my"
          />
          <Tile
            icon={<Package className="w-5 h-5" />}
            label="Pack"
            hint="Scan to seal a Produced pallet"
            onClick={() => navigate("/mobile/pack")}
            testId="tile-pack"
          />
          <Tile
            icon={<Activity className="w-5 h-5" />}
            label="Live Production"
            hint="Today's output summary"
            onClick={() => navigate("/mobile/live")}
            testId="tile-live"
          />
          {perms.includes("inventory.view") && (
            <Tile
              icon={<Boxes className="w-5 h-5" />}
              label="Inventory"
              hint="Live stock on hand"
              onClick={() => navigate("/mobile/inventory")}
              testId="tile-inventory"
            />
          )}
          {(perms.includes("pallet.view") || perms.includes("stock.count")) && (
            <Tile
              icon={<ScanLine className="w-5 h-5" />}
              label="Stock Count"
              hint="Scan pallets to count stock"
              onClick={() => navigate("/mobile/stock-count")}
              testId="tile-stock-count"
            />
          )}
        </div>
      </div>
    </>
  );
}

function Tile({ icon, label, hint, disabled, onClick, testId }) {
  return (
    <button
      className={`m-tile ${disabled ? "m-tile-disabled" : ""}`}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
    >
      <span className="m-tile-icon">{icon}</span>
      <span className="m-tile-label">{label}</span>
      <span className="m-tile-hint">{hint}</span>
    </button>
  );
}

function Stat({ label, value, color, testId }) {
  const display = value === undefined || value === null ? "—" : value;
  return (
    <div data-testid={testId}>
      <div style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 20, color, lineHeight: 1 }}>
        {display}
      </div>
      <div style={{ fontSize: 10, color: "#a8a29e", textTransform: "uppercase", letterSpacing: 0.6, marginTop: 4 }}>
        {label}
      </div>
    </div>
  );
}

// ============================== Produce flow ==============================

function Produce({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("scan-product"); // scan-product | scan-pallet | pick-po | produced | paired-tap-produce
  const [product, setProduct] = useState(null);
  const [palletId, setPalletId] = useState(null);
  const [lastProductPayload, setLastProductPayload] = useState(null); // raw QR text from step 1 — passed to step 2 scanner so it doesn't re-read the same sticker
  const [pos, setPos] = useState([]);
  const [selectedPo, setSelectedPo] = useState(null);
  const [pallet, setPallet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [perms, setPerms] = useState([]);
  const [qtyOverride, setQtyOverride] = useState(""); // custom per-pallet qty (string for input)
  const [overrideReason, setOverrideReason] = useState("");
  const canOverrideQty = perms.includes("pallet.override_qty");
  // Bump to force the active Scanner to fully remount (hard reset — only used on
  // double-tap of Rescan or as a last-resort fallback). Normal Rescan calls
  // scannerRef.resume() which is much lighter (no camera teardown).
  const [scanNonce, setScanNonce] = useState(0);
  const productScannerRef = useRef(null);
  const palletScannerRef = useRef(null);
  const lastRescanTsRef = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.permissions();
        setPerms(r?.effective_permissions || []);
      } catch {
        setPerms([]);
      }
    })();
  }, []);

  const rescan = (which) => {
    setErr(null);
    const refObj = which === "pallet" ? palletScannerRef : productScannerRef;
    const now = Date.now();
    // Double-tap within 2 s ⇒ hard remount (full camera reset)
    if (now - lastRescanTsRef.current < 2000) {
      lastRescanTsRef.current = 0;
      setScanNonce((n) => n + 1);
      return;
    }
    lastRescanTsRef.current = now;
    // Otherwise soft-resume the running camera
    if (refObj.current?.resume) {
      refObj.current.resume();
    } else {
      setScanNonce((n) => n + 1);
    }
  };

  const handleProductDecoded = async (text) => {
    setErr(null);
    const code = extractProductCode(text);
    if (!code) {
      setErr("Could not read a product code from this QR.");
      productScannerRef.current?.resume?.();
      return;
    }
    setBusy(true);
    try {
      const p = await api.productByCode(code);
      if (!p.default_qty_per_pallet || p.default_qty_per_pallet <= 0) {
        setErr(`Product ${code} has no Qty/Pallet set. Ask admin to configure it.`);
        productScannerRef.current?.resume?.();
        return;
      }
      setProduct(p);
      setLastProductPayload(String(text || ""));
      try {
        const polist = await api.poPicker(code);
        setPos(polist);
        setSelectedPo(polist[0] || null); // oldest pre-selected
      } catch {
        setPos([]);
        setSelectedPo(null);
      }
      setStep("scan-pallet");
    } catch (ex) {
      setErr(ex.response?.data?.detail || `Product ${code} not found in catalog`);
      productScannerRef.current?.resume?.();
    } finally {
      setBusy(false);
    }
  };

  const handlePalletDecoded = async (text) => {
    setErr(null);
    const raw = String(text || "").trim();
    if (!isPalletId(raw)) {
      setErr(`"${raw}" is not a pallet QR. Expected format PLT-YYYY-NNNNNN.`);
      palletScannerRef.current?.resume?.();
      return;
    }
    setBusy(true);
    try {
      // Pre-validate state so the operator gets the error during scan, not at submit.
      const existing = await api.getPallet(raw);
      if (!["PRINTED", "VOIDED"].includes(existing.state)) {
        setErr(
          `Pallet ${raw} is already ${existing.state}` +
          (existing.product_code ? ` (${existing.product_code}` + (existing.po_no ? ` · PO ${existing.po_no})` : ")") : "") +
          ". Scan a different pallet."
        );
        palletScannerRef.current?.resume?.();
        return;
      }
      setPalletId(raw);
      setStep("pick-po");
    } catch (ex) {
      if (ex.response?.status === 404) {
        setErr(`Pallet ${raw} not found in system. Print a new sticker batch first.`);
      } else {
        setErr(ex.response?.data?.detail || "Could not validate pallet");
      }
      palletScannerRef.current?.resume?.();
    } finally {
      setBusy(false);
    }
  };

  const confirmPair = async () => {
    if (!product || !palletId) return;
    setBusy(true);
    setErr(null);
    try {
      const p = await api.pair({
        pallet_id: palletId,
        product_code: product.product_code,
        po_id: selectedPo?.po_id || null,
        device_id: DEVICE_ID,
        target_qty_override:
          canOverrideQty && qtyOverride && Number(qtyOverride) > 0
            ? Number(qtyOverride)
            : null,
        override_reason:
          canOverrideQty && qtyOverride && Number(qtyOverride) > 0
            ? overrideReason.trim() || null
            : null,
      });
      setPallet(p);
      setStep("paired-tap-produce");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Pairing failed");
    } finally {
      setBusy(false);
    }
  };

  const markProduced = async () => {
    if (!pallet) return;
    setBusy(true);
    setErr(null);
    try {
      const p = await api.produce(pallet.id, null, null); // default = target_qty
      setPallet(p);
      setStep("produced");
      toast.success(`${pallet.id} produced · ${p.actual_qty} pcs`);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not mark Produced");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep("scan-product");
    setProduct(null);
    setPalletId(null);
    setLastProductPayload(null);
    setPos([]);
    setSelectedPo(null);
    setPallet(null);
    setErr(null);
    setManualMode(false);
    setManualInput("");
    setQtyOverride("");
    setOverrideReason("");
  };

  return (
    <>
      <SimpleChrome title="Produce" onBack={() => navigate("/mobile")} />
      <div className="m-screen">
        <span className="m-step-pill" data-testid="produce-step">{stepLabel(step)}</span>

        {err && <div className="m-error-banner" data-testid="produce-error">{err}</div>}

        {step === "scan-product" && (
          <>
            <h3 style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 18, margin: "14px 0 10px" }}>
              Step 1 — Scan PRODUCT QR
            </h3>
            {!manualMode ? (
              <Scanner ref={productScannerRef} key={`prod-${scanNonce}`} onDecoded={handleProductDecoded} hint="Aim camera at the product's QR sticker" />
            ) : (
              <ManualEntry
                label="Type product code (e.g. CG-4)"
                value={manualInput}
                onChange={setManualInput}
                onSubmit={() => handleProductDecoded(manualInput)}
              />
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 8 }}>
              {!manualMode && (
                <button
                  className="m-link-btn"
                  onClick={() => rescan("product")}
                  data-testid="rescan-product"
                >
                  ↻ Rescan
                </button>
              )}
              <button
                className="m-link-btn"
                onClick={() => setManualMode((v) => !v)}
                data-testid="toggle-manual-product"
              >
                <Keyboard className="w-3 h-3 inline mr-1" />
                {manualMode ? "Use camera" : "Type code instead"}
              </button>
            </div>
          </>
        )}

        {step === "scan-pallet" && product && (
          <>
            <ProductCard product={product} />
            <h3 style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 18, margin: "14px 0 10px" }}>
              Step 2 — Scan PALLET QR
            </h3>
            {!manualMode ? (
              <Scanner
                ref={palletScannerRef}
                key={`pallet-${scanNonce}`}
                onDecoded={handlePalletDecoded}
                hint="Aim at the pallet sticker (PLT-…)"
                ignoreText={lastProductPayload}
              />
            ) : (
              <ManualEntry
                label="Type pallet ID (e.g. PLT-2026-000123)"
                value={manualInput}
                onChange={setManualInput}
                onSubmit={() => handlePalletDecoded(manualInput.toUpperCase())}
              />
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 8 }}>
              {!manualMode && (
                <button
                  className="m-link-btn"
                  onClick={() => rescan("pallet")}
                  data-testid="rescan-pallet"
                >
                  ↻ Rescan
                </button>
              )}
              <button
                className="m-link-btn"
                onClick={() => setManualMode((v) => !v)}
                data-testid="toggle-manual-pallet"
              >
                <Keyboard className="w-3 h-3 inline mr-1" />
                {manualMode ? "Use camera" : "Type ID instead"}
              </button>
            </div>
          </>
        )}

        {step === "pick-po" && product && palletId && (
          <>
            <ProductCard product={product} />
            <div className="m-card" data-testid="pallet-card">
              <div className="m-card-label">Pallet</div>
              <div className="m-card-mono">{palletId}</div>
            </div>
            <h3 style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 18, margin: "14px 0 6px" }}>
              Step 3 — Tag a PO (optional)
            </h3>
            <p style={{ fontSize: 12, color: "#a8a29e", marginBottom: 4 }}>
              Oldest PO is pre-selected. Tap any other or Skip.
            </p>
            <PoPicker
              pos={pos}
              selected={selectedPo}
              onSelect={setSelectedPo}
              onSkip={() => setSelectedPo(null)}
            />
            {canOverrideQty && (
              <div className="m-card" data-testid="qty-override-section" style={{ marginTop: 12 }}>
                <div className="m-card-label">Qty for this pallet (optional override)</div>
                <input
                  type="number"
                  inputMode="numeric"
                  min="1"
                  className="m-input"
                  data-testid="qty-override-input"
                  placeholder={`Default ${product.default_qty_per_pallet} pcs`}
                  value={qtyOverride}
                  onChange={(e) => setQtyOverride(e.target.value)}
                  style={{
                    width: "100%", marginTop: 6, padding: "10px 12px",
                    background: "#1c1917", color: "#fafaf9", border: "1px solid #44403c",
                    borderRadius: 8, fontSize: 18, fontWeight: 700,
                  }}
                />
                <div style={{ color: "#a8a29e", fontSize: 12, marginTop: 6 }}>
                  Leave blank to use the catalog default of {product.default_qty_per_pallet} pcs.
                  This only affects this one pallet.
                </div>
                {qtyOverride && Number(qtyOverride) > 0 && (
                  <>
                    <div className="m-card-label" style={{ marginTop: 12 }}>Reason (optional)</div>
                    <input
                      type="text"
                      className="m-input"
                      data-testid="qty-override-reason-input"
                      placeholder="e.g. Short run for Customer X"
                      value={overrideReason}
                      maxLength={120}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      style={{
                        width: "100%", marginTop: 6, padding: "10px 12px",
                        background: "#1c1917", color: "#fafaf9", border: "1px solid #44403c",
                        borderRadius: 8, fontSize: 15,
                      }}
                    />
                    <div style={{ color: "#a8a29e", fontSize: 12, marginTop: 6 }}>
                      Shown on the pallet card and in reconciliation/aging reports.
                    </div>
                  </>
                )}
              </div>
            )}
            <button
              className="m-btn"
              style={{ marginTop: 14 }}
              onClick={confirmPair}
              disabled={busy}
              data-testid="confirm-pair"
            >
              {busy
                ? "Pairing…"
                : (() => {
                    const qtyLabel =
                      canOverrideQty && qtyOverride && Number(qtyOverride) > 0
                        ? ` · ${Number(qtyOverride)} pcs`
                        : "";
                    return selectedPo
                      ? `Confirm — pair with PO ${selectedPo.po_number}${qtyLabel}`
                      : `Confirm — Unassigned${qtyLabel}`;
                  })()}
            </button>
          </>
        )}

        {step === "paired-tap-produce" && pallet && (
          <>
            <div className="m-card" data-testid="paired-card">
              <div className="m-card-label">Pallet · {pallet.state}</div>
              <div className="m-card-mono">{pallet.id}</div>
              <div className="m-divider" />
              <div className="m-card-label">Product</div>
              <div className="m-card-value">{pallet.product_code}</div>
              <div className="m-divider" />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div className="m-card-label">Target Qty{pallet.qty_overridden ? " · custom" : ""}</div>
                  <div className="m-card-value">{pallet.target_qty}</div>
                  {pallet.qty_overridden && pallet.qty_override_reason && (
                    <div style={{ color: "#a8a29e", fontSize: 12 }}>{pallet.qty_override_reason}</div>
                  )}
                </div>
                <div>
                  <div className="m-card-label">PO</div>
                  <div className="m-card-mono" style={{ fontSize: 14 }}>{pallet.po_no || "Unassigned"}</div>
                </div>
              </div>
            </div>
            <p style={{ color: "#a8a29e", fontSize: 13, margin: "12px 0" }}>
              Stack {pallet.target_qty} pieces on this pallet, then tap Produced.
            </p>
            <button
              className="m-btn"
              onClick={markProduced}
              disabled={busy}
              data-testid="tap-produced"
            >
              <CheckCircle2 className="w-4 h-4 inline mr-1" />
              {busy ? "Saving…" : `Produced · ${pallet.target_qty} pcs`}
            </button>
            <button
              className="m-btn m-btn--ghost"
              style={{ marginTop: 8 }}
              onClick={reset}
              data-testid="produce-next"
            >
              Pair next pallet
            </button>
          </>
        )}

        {step === "produced" && pallet && (
          <>
            <div className="m-success-banner" data-testid="produced-banner">
              <CheckCircle2 className="w-4 h-4 inline mr-1" />
              {pallet.id} produced · {pallet.actual_qty} pcs
            </div>
            <button className="m-btn" onClick={reset} data-testid="produce-next-2">
              Pair next pallet
            </button>
            <button className="m-btn m-btn--ghost" style={{ marginTop: 8 }} onClick={() => navigate("/mobile")}>
              Back to home
            </button>
          </>
        )}
      </div>
    </>
  );
}

function stepLabel(s) {
  switch (s) {
    case "scan-product": return "Step 1 of 3";
    case "scan-pallet": return "Step 2 of 3";
    case "pick-po": return "Step 3 of 3";
    case "paired-tap-produce": return "Ready · awaiting Produced tap";
    case "produced": return "Done";
    default: return "";
  }
}

function ProductCard({ product }) {
  return (
    <div className="m-card" data-testid="product-card">
      <div className="m-card-label">Product</div>
      <div className="m-card-value">{product.product_code}</div>
      {product.description && <div style={{ color: "#a8a29e", fontSize: 13 }}>{product.description}</div>}
      <div className="m-divider" />
      <div className="m-card-label">Qty per pallet</div>
      <div className="m-card-value">{product.default_qty_per_pallet} pcs</div>
    </div>
  );
}

function PoPicker({ pos, selected, onSelect, onSkip }) {
  return (
    <div className="m-po-list">
      <button
        className={`m-po-item m-po-item--skip ${selected ? "" : "m-po-item--selected"}`}
        onClick={onSkip}
        data-testid="po-skip"
      >
        <div>
          <div className="m-po-num">Skip — Unassigned</div>
          <div className="m-po-meta">Send to inventory without a PO; assign later</div>
        </div>
        {!selected && <CheckCircle2 className="w-5 h-5 text-orange-500" />}
      </button>
      {pos.length === 0 && (
        <div className="m-error-banner" style={{ background: "rgba(120, 53, 15, 0.18)", borderColor: "#92400e", color: "#fde68a" }}>
          <AlertTriangle className="w-4 h-4 inline mr-1" />
          No open POs for this product right now. Tap Confirm to send to Unassigned inventory.
        </div>
      )}
      {pos.map((po) => (
        <button
          key={po.po_id}
          className={`m-po-item ${selected?.po_id === po.po_id ? "m-po-item--selected" : ""}`}
          onClick={() => onSelect(po)}
          data-testid={`po-pick-${po.po_number}`}
        >
          <div>
            <div className="m-po-num">{po.po_number}</div>
            <div className="m-po-meta">
              {po.customer_name || "—"} · ordered {po.order_date || "—"}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="m-po-pending">{po.pending_qty}</div>
            <div className="m-po-meta">pending</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ManualEntry({ label, value, onChange, onSubmit }) {
  return (
    <div className="m-card">
      <div className="m-card-label" style={{ marginBottom: 8 }}>{label}</div>
      <input
        className="m-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus
        inputMode="numeric"
        pattern="[0-9]*"
        data-testid="manual-entry-input"
      />
      <button
        className="m-btn"
        style={{ marginTop: 12 }}
        onClick={onSubmit}
        disabled={!value.trim()}
        data-testid="manual-entry-submit"
      >
        Use this
      </button>
    </div>
  );
}

function SimpleChrome({ title, onBack }) {
  return (
    <div className="m-chrome">
      {onBack && (
        <button className="m-back-btn" onClick={onBack} data-testid="mobile-back">
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}
      <div>
        <div className="m-chrome-title">{title}</div>
      </div>
    </div>
  );
}

// ============================== Stock Count (scan-based physical count) ==============================

// Distinct audio + haptic cues per scan result so operators can keep scanning
// without watching the screen. Sound is gated by a user toggle (localStorage).
let _scAudioCtx = null;
function _scTone(freq, start, dur, ctx, type = "sine", gainVal = 0.16) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const t0 = ctx.currentTime + start;
  gain.gain.setValueAtTime(gainVal, t0);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + dur);
}
function playScanFeedback(kind, soundOn) {
  try {
    if (navigator.vibrate) {
      if (kind === "success") navigator.vibrate(60);
      else if (kind === "duplicate") navigator.vibrate([40, 50, 40]);
      else navigator.vibrate([120, 70, 120]);
    }
  } catch { /* ignore */ }
  if (!soundOn) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!_scAudioCtx) _scAudioCtx = new Ctx();
    const ctx = _scAudioCtx;
    if (ctx.state === "suspended") ctx.resume();
    if (kind === "success") { _scTone(880, 0, 0.09, ctx); _scTone(1320, 0.09, 0.13, ctx); }
    else if (kind === "duplicate") { _scTone(520, 0, 0.1, ctx, "square", 0.1); _scTone(520, 0.15, 0.1, ctx, "square", 0.1); }
    else { _scTone(200, 0, 0.34, ctx, "sawtooth", 0.13); }
  } catch { /* ignore */ }
}

function SCSheet({ title, onClose, children, testId }) {
  return (
    <div data-testid={testId} onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 520, background: "#1c1917", borderTopLeftRadius: 18, borderTopRightRadius: 18,
          borderTop: "1px solid rgba(255,255,255,0.1)", padding: 18, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#f1f5f9" }}>{title}</div>
          <button onClick={onClose} data-testid={`${testId}-close`}
            style={{ background: "transparent", border: "none", color: "#a8a29e", cursor: "pointer", padding: 4, lineHeight: 0 }}>
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function StockCountMobile({ user }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [manualInput, setManualInput] = useState("");
  const [scanNonce] = useState(0);
  const [lastScan, setLastScan] = useState(null);
  const [zone, setZone] = useState(() => localStorage.getItem("scm_zone") || "");
  const [zoneLocked, setZoneLocked] = useState(() => localStorage.getItem("scm_zone_locked") === "1");
  const [showFinalize, setShowFinalize] = useState(false);
  const [mProduct, setMProduct] = useState("");
  const [mBy, setMBy] = useState("pallets"); // "pallets" | "pieces"
  const [mPallets, setMPallets] = useState("");
  const [mPcsPer, setMPcsPer] = useState("");
  const [mQty, setMQty] = useState("");
  const [codes, setCodes] = useState([]);
  const [perms, setPerms] = useState([]);
  const [scanQuery, setScanQuery] = useState("");
  const [scanExcludedOnly, setScanExcludedOnly] = useState(false);
  const [scanZone, setScanZone] = useState("");
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("scm_sound_off") !== "1");
  const [shortCandidates, setShortCandidates] = useState([]);
  const [zones, setZones] = useState([]);
  const [resolveMode, setResolveMode] = useState(false);
  const [sheet, setSheet] = useState(null); // null | 'type' | 'noqr' | 'counts'
  const [photos, setPhotos] = useState([]); // [{ blob, url }] pending unidentified capture
  const [photoNote, setPhotoNote] = useState("");
  const [uResolve, setUResolve] = useState({}); // uid -> { q, code, qty } — mobile resolve form state
  const [lightbox, setLightbox] = useState(null); // { pics: [fileId], i } — full-size photo viewer
  const scannerRef = useRef(null);

  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      localStorage.setItem("scm_sound_off", next ? "0" : "1");
      if (next) playScanFeedback("success", true); // preview the cue when enabling
      return next;
    });
  };

  useEffect(() => { api.productCodes().then(setCodes).catch(() => {}); }, []);
  useEffect(() => { api.permissions().then((r) => setPerms(r?.effective_permissions || [])).catch(() => {}); }, []);
  useEffect(() => { api.scListZones().then((r) => setZones(r.zones || [])).catch(() => {}); }, []);
  const canManage = perms.includes("inventory.manage") || perms.includes("stock.count");

  const selectZone = (name) => {
    if (name === zone) return;
    const hasScans = (active?.scans || []).length > 0;
    if (zoneLocked &&
        !window.confirm(`Zone "${zone}" is locked.\nUnlock and switch to "${name}"?`)) {
      return;
    }
    if (!zoneLocked && zone && hasScans &&
        !window.confirm(`Change counting zone from "${zone}" to "${name}"?\nNew scans will be recorded under "${name}".`)) {
      return;
    }
    setZone(name);
    localStorage.setItem("scm_zone", name);
  };

  const toggleZoneLock = () => {
    if (!zone) { toast.warning("Pick a zone first, then lock it"); return; }
    setZoneLocked((v) => {
      const next = !v;
      localStorage.setItem("scm_zone_locked", next ? "1" : "0");
      toast.success(next ? `Zone "${zone}" locked` : "Zone unlocked");
      return next;
    });
  };

  // Hard rule: a zone must be chosen before anything is recorded — no "Unzoned".
  const ensureZone = () => {
    if (zone) return true;
    toast.error("Select a zone first — counting without a zone is disabled");
    return false;
  };

  const addSkip = async () => {
    if (!ensureZone()) return;
    try {
      await api.scSkip(active.session.id, zone, 1);
      playScanFeedback("duplicate", soundOn);
      await openSession(active.session.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to skip"); }
  };

  const resolveSkip = async () => {
    if (!zone) return;
    try {
      await api.scSkip(active.session.id, zone, -1);
      await openSession(active.session.id);
    } catch { /* ignore */ }
  };

  const snapPhoto = async () => {
    if (!ensureZone()) return;
    try {
      const blob = await scannerRef.current?.captureFrame?.();
      if (!blob) { toast.error("Camera not ready"); return; }
      playScanFeedback("duplicate", soundOn);
      setPhotos((arr) => [...arr, { blob, url: URL.createObjectURL(blob) }]);
    } catch { toast.error("Could not capture photo"); }
  };

  const removePhoto = (idx) => {
    setPhotos((arr) => {
      const p = arr[idx];
      if (p?.url) URL.revokeObjectURL(p.url);
      return arr.filter((_, i) => i !== idx);
    });
  };

  const discardPhotos = () => {
    photos.forEach((p) => p.url && URL.revokeObjectURL(p.url));
    setPhotos([]);
    setPhotoNote("");
    scannerRef.current?.resume?.();
  };

  const savePhoto = async () => {
    if (!photos.length || !zone) return;
    setBusy(true);
    try {
      await api.scAddUnidentified(active.session.id, photos.map((p) => p.blob), zone, photoNote.trim());
      toast.success(`Unidentified item logged in ${zone}`);
      discardPhotos();
      await openSession(active.session.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save photo"); }
    finally { setBusy(false); }
  };

  const deleteUnident = async (uid) => {
    if (!window.confirm("Remove this unidentified item?")) return;
    try {
      await api.scDeleteUnidentified(active.session.id, uid);
      await openSession(active.session.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove"); }
  };

  const setUr = (uid, patch) => setUResolve((s) => ({ ...s, [uid]: { ...(s[uid] || {}), ...patch } }));
  const pickResolveCode = async (uid, code) => {
    setUr(uid, { code, q: code });
    try {
      const h = await api.scProductHint(code);
      if (h?.suggested_pcs_per_pallet) {
        setUResolve((s) => {
          const cur = s[uid] || {};
          // only prefill if the operator hasn't already typed a pcs/pallet
          return { ...s, [uid]: { ...cur, pcsPer: cur.pcsPer || String(h.suggested_pcs_per_pallet) } };
        });
      }
    } catch { /* ignore */ }
  };
  const resolveUnident = async (uid) => {
    const ri = uResolve[uid] || {};
    if (!ri.code) { toast.error("Pick a product code"); return; }
    const pallets = Number(ri.pallets) || 0;
    const pcsPer = Number(ri.pcsPer) || 0;
    const directQty = Number(ri.qty) || 0;
    let payload;
    if (pallets > 0) {
      if (pcsPer <= 0) { toast.error("Enter pcs per pallet"); return; }
      payload = { product_code: ri.code, pallets, pcs_per_pallet: pcsPer };
    } else if (directQty > 0) {
      payload = { product_code: ri.code, qty: directQty };
    } else {
      toast.error("Enter number of pallets or a piece quantity"); return;
    }
    setBusy(true);
    try {
      await api.scResolveUnidentified(active.session.id, uid, payload);
      toast.success("Identified & rolled into the count");
      setUResolve((s) => { const n = { ...s }; delete n[uid]; return n; });
      await openSession(active.session.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Resolve failed"); }
    finally { setBusy(false); }
  };
  const resolveNote = async (uid) => {
    const ri = uResolve[uid] || {};
    const note = (ri.noteText || "").trim();
    if (!note) { toast.error("Enter a note"); return; }
    setBusy(true);
    try {
      await api.scResolveUnidentifiedNote(active.session.id, uid, note);
      toast.success("Closed with a note (not counted)");
      setUResolve((s) => { const n = { ...s }; delete n[uid]; return n; });
      await openSession(active.session.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save note"); }
    finally { setBusy(false); }
  };
  const resolvePallet = async (uid, palletId = null) => {
    const ri = uResolve[uid] || {};
    const short = (ri.shortId || "").trim();
    if (!palletId && !short) { toast.error("Enter the pallet's short number"); return; }
    setBusy(true);
    try {
      const res = await api.scResolveUnidentifiedPallet(active.session.id, uid, palletId ? { pallet_id: palletId } : { short });
      if (res.needs_disambiguation) {
        setUr(uid, { palletCandidates: res.candidates });
        toast("Multiple pallets match — pick one below");
        return;
      }
      toast.success(`Linked to ${res.pallet_id} · ${res.product_code || "(no product)"} (${res.qty || 0} pcs)${res.already_scanned ? " — already counted" : ""}`);
      setUResolve((s) => { const n = { ...s }; delete n[uid]; return n; });
      await openSession(active.session.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Resolve failed"); }
    finally { setBusy(false); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this stock count session and all its scans? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.scDeleteSession(id);
      toast.success("Count deleted");
      setActive(null);
      await loadSessions();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
    finally { setBusy(false); }
  };

  const fetchHint = async (code) => {
    if (!code.trim()) return;
    try {
      const h = await api.scProductHint(code.trim());
      if (h.suggested_pcs_per_pallet && !mPcsPer) setMPcsPer(String(h.suggested_pcs_per_pallet));
    } catch { /* ignore */ }
  };

  const submitManual = async () => {
    const code = mProduct.trim();
    if (!code) { setErr("Enter a product code"); return; }
    if (!codes.includes(code)) { setErr(`"${code}" is not in the system — pick a code from the list`); return; }
    if (!ensureZone()) return;
    const body = { product_code: code, zone: zone.trim() || undefined };
    if (mBy === "pallets") {
      if (!Number(mPallets) || !Number(mPcsPer)) { setErr("Enter pallets and pcs/pallet"); return; }
      body.pallets = Number(mPallets);
      body.pcs_per_pallet = Number(mPcsPer);
    } else {
      if (!Number(mQty)) { setErr("Enter a piece qty"); return; }
      body.qty = Number(mQty);
    }
    setBusy(true); setErr(null);
    try {
      const r = await api.scManual(active.session.id, body);
      toast.success(`Added ${r.scan.qty} ${code}${r.scan.manual_pallets ? ` (${r.scan.manual_pallets} pal)` : ""}`);
      playScanFeedback("success", soundOn);
      if (resolveMode && zone && (active?.skips?.[zone] || 0) > 0) {
        try { await api.scSkip(active.session.id, zone, -1); toast.info(`Resolved 1 skipped in ${zone}`); } catch { /* ignore */ }
      }
      setMProduct(""); setMPallets(""); setMPcsPer(""); setMQty("");
      await openSession(active.session.id);
      setSheet(null);
    } catch (e) { setErr(e.response?.data?.detail || "Failed to add"); }
    finally { setBusy(false); }
  };

  const loadSessions = async () => {
    setLoading(true);
    try { const d = await api.scListSessions(); setSessions(d.items || []); }
    catch { setErr("Failed to load sessions"); }
    finally { setLoading(false); }
  };
  const openSession = async (id) => {
    try { const d = await api.scGetSession(id); setActive(d); setErr(null); }
    catch { setErr("Failed to open session"); }
  };
  useEffect(() => { loadSessions(); }, []);

  const create = async () => {
    setBusy(true);
    try { const s = await api.scCreateSession(""); await openSession(s.id); toast.success("Count started"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const onScan = async (raw, captureMethod = "qr") => {
    const pid = String(raw || "").trim().toUpperCase();
    if (!isPalletId(pid)) {
      setErr(`"${pid}" is not a pallet QR (PLT-YYYY-NNNNNN).`);
      scannerRef.current?.resume?.();
      return;
    }
    if (!ensureZone()) { scannerRef.current?.resume?.(); return; }
    const preSkips = active?.skips?.[zone] || 0;
    setBusy(true); setErr(null);
    try {
      const r = await api.scScan(active.session.id, pid, zone.trim(), captureMethod);
      if (r.duplicate) { setLastScan({ dup: true, pid }); playScanFeedback("duplicate", soundOn); }
      else {
        const s = r.scan;
        setLastScan(s);
        playScanFeedback(s.included ? "success" : "excluded", soundOn);
        if (resolveMode && zone && preSkips > 0) {
          try { await api.scSkip(active.session.id, zone, -1); toast.info(`Resolved 1 skipped in ${zone}`); } catch { /* ignore */ }
        }
      }
      await openSession(active.session.id);
      setSheet(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Scan failed");
    } finally {
      setBusy(false);
      setManualInput("");
      scannerRef.current?.resume?.();
    }
  };

  // Handle a typed entry that may be a full pallet ID or just the short number
  // visible on the sticker (used when the QR is unreadable through stretch-wrap).
  const submitTypedId = async (raw) => {
    const v = String(raw || "").trim().toUpperCase();
    if (!v) { setErr("Enter a pallet ID or the number on the sticker"); return; }
    setShortCandidates([]);
    if (isPalletId(v)) { await onScan(v, "typed_id"); return; }
    const digits = v.replace(/\D/g, "");
    if (!digits) { setErr("Enter the pallet number shown on the sticker (e.g. 123)"); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api.scResolveShort(digits);
      const cands = r.candidates || [];
      if (cands.length === 0) {
        setErr(`No pallet found ending in ${digits}`);
        playScanFeedback("excluded", soundOn);
      } else {
        // Always show the match(es) so the operator can confirm the product
        // before recording — catches mis-keyed sticker numbers on the floor.
        setShortCandidates(cands);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const pickCandidate = async (id) => {
    setShortCandidates([]);
    setManualInput("");
    await onScan(id, "short_pick");
  };

  const undoLast = async () => {
    setBusy(true);
    try {
      const r = await api.scUndoLast(active.session.id);
      const u = r.undone || {};
      toast.success(`Undid ${u.product_code || u.pallet_id || "last scan"}${u.qty ? ` (−${u.qty} pcs)` : ""}`);
      playScanFeedback("duplicate", soundOn);
      setLastScan(null);
      await openSession(active.session.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Nothing to undo"); }
    finally { setBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const blob = await api.scExportCsvBlob(active.session.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (active.session.name || "session").replace(/[^A-Za-z0-9._-]+/g, "_");
      a.download = `stock-count-${safe}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
  };

  const finalize = async () => {
    setBusy(true);
    try {
      await api.scFinalize(active.session.id);
      setShowFinalize(false);
      toast.success("Reconciliation generated — open it on desktop to view drill-down");
      await openSession(active.session.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Finalize failed"); }
    finally { setBusy(false); }
  };

  const sm = active?.summary || {};
  const isOpen = active?.session?.status === "open";
  const zoneSkips = zone ? (active?.skips?.[zone] || 0) : 0;
  const totalSkips = sm.total_skips || 0;
  const zoneRow = zone ? (active?.per_zone || []).find((z) => z.zone === zone) : null;
  const zoneCounted = zoneRow ? (zoneRow.pallets || 0) + (zoneRow.loose || 0) : 0;
  const cov = sm.coverage_pct || 0;
  const left = Math.max(0, (sm.expected_pallets || 0) - (sm.scanned_expected || 0));
  const scmActionBtn = {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
    padding: "10px 4px", borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: "pointer",
    border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "#e2e8f0",
  };

  // Live elapsed timer for the active count session
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isOpen, active?.session?.id]);

  const fmtDuration = (secs) => {
    if (secs == null || secs < 0) return "—";
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h > 0 ? `${h}h ` : ""}${m}m ${String(s).padStart(2, "0")}s`;
  };

  const startMs = active?.session ? Date.parse(active.session.created_at || active.session.as_of) : 0;
  const endMs = !active?.session ? nowTs
    : isOpen ? nowTs
    : (active.session.closed_at ? Date.parse(active.session.closed_at) : nowTs);
  const elapsedSecs = startMs ? Math.max(0, Math.floor((endMs - startMs) / 1000)) : 0;

  // Keep the big scan confirmation on screen for 3 seconds
  useEffect(() => {
    if (!lastScan) return;
    const id = setTimeout(() => setLastScan(null), 3000);
    return () => clearTimeout(id);
  }, [lastScan]);

  const zonesList = [...new Set((active?.scans || []).map((s) => s.zone).filter(Boolean))];
  const filteredScans = (active?.scans || []).filter((s) => {
    if (scanExcludedOnly && s.included) return false;
    if (scanZone && (s.zone || "Unzoned") !== scanZone) return false;
    const q = scanQuery.trim().toLowerCase();
    if (q && !((s.product_code || "").toLowerCase().includes(q) || (s.pallet_id || "").toLowerCase().includes(q))) return false;
    return true;
  });
  // When a zone filter is active, order the scans by their walk sequence (the order
  // they were physically scanned in that zone) so the list mirrors the floor walk.
  if (scanZone) {
    filteredScans.sort((a, b) => (a.walk_no || 0) - (b.walk_no || 0));
  }

  return (
    <>
      {lightbox && (
        <div data-testid="scm-lightbox" onClick={() => setLightbox(null)}
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.94)",
            display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
          <button onClick={() => setLightbox(null)} data-testid="scm-lightbox-close"
            style={{ position: "absolute", top: 14, right: 14, width: 40, height: 40, borderRadius: 999,
              border: "none", background: "rgba(255,255,255,0.14)", color: "#fff", display: "flex",
              alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <X style={{ width: 22, height: 22 }} />
          </button>
          <ZoomableImage src={api.scPhotoUrl(lightbox.pics[lightbox.i])} alt="unidentified pallet" testId="scm-lightbox-img" />
          {lightbox.pics.length > 1 && (
            <div onClick={(e) => e.stopPropagation()}
              style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 16 }}>
              <button data-testid="scm-lightbox-prev"
                onClick={() => setLightbox((l) => ({ ...l, i: (l.i - 1 + l.pics.length) % l.pics.length }))}
                style={{ width: 44, height: 44, borderRadius: 999, border: "none", background: "rgba(255,255,255,0.14)", color: "#fff", cursor: "pointer" }}>
                <ChevronLeft style={{ width: 24, height: 24 }} />
              </button>
              <span style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>{lightbox.i + 1} / {lightbox.pics.length}</span>
              <button data-testid="scm-lightbox-next"
                onClick={() => setLightbox((l) => ({ ...l, i: (l.i + 1) % l.pics.length }))}
                style={{ width: 44, height: 44, borderRadius: 999, border: "none", background: "rgba(255,255,255,0.14)", color: "#fff", cursor: "pointer" }}>
                <ChevronRight style={{ width: 24, height: 24 }} />
              </button>
            </div>
          )}
          <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, marginTop: 10 }} data-testid="scm-lightbox-hint">Pinch or double-tap to zoom · tap outside to close</div>
        </div>
      )}
      <SimpleChrome title="Stock Count" onBack={() => (active ? (setActive(null), loadSessions()) : navigate("/mobile"))} />
      <div className="m-screen">
        {err && <div className="m-error-banner" data-testid="sc-error">{err}</div>}

        {!active ? (
          <>
            <button className="m-btn" onClick={create} disabled={busy} data-testid="scm-create-btn" style={{ marginBottom: 14 }}>
              <Plus className="w-4 h-4 inline mr-1" /> Start new count
            </button>
            <div className="m-card-label" style={{ marginBottom: 8 }}>Recent sessions</div>
            {loading && <Loader2 className="animate-spin mx-auto mt-6" />}
            {!loading && sessions.length === 0 && <div style={{ color: "#a8a29e", fontSize: 13 }}>No sessions yet.</div>}
            {sessions.map((s) => (
              <button key={s.id} className="m-list-row" onClick={() => openSession(s.id)} data-testid={`scm-session-${s.id}`}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10,
                  padding: "10px 12px", marginBottom: 8, textAlign: "left", cursor: "pointer" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: "#a8a29e" }}>{fmtDateTime(s.as_of)} · {s.scan_count} scans{s.status !== "open" && s.duration_seconds != null ? ` · ${fmtDuration(s.duration_seconds)}` : ""}</div>
                  {s.created_by_name && (
                    <div style={{ fontSize: 11, color: "#78716c" }} data-testid={`scm-session-creator-${s.id}`}>by {s.created_by_name}</div>
                  )}
                </div>
                <span style={{ fontSize: 11, color: s.status === "open" ? "#34d399" : "#94a3b8" }}>{s.status}</span>
              </button>
            ))}
          </>
        ) : (
          <>
            {/* Slim status strip — coverage / scanned / left / timer / sound */}
            <div data-testid="scm-sticky-progress" style={{
              position: "sticky", top: 58, zIndex: 30, marginBottom: 8,
              background: "rgba(28,25,23,0.94)", backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "6px 10px",
              display: "flex", alignItems: "center", gap: 8 }}>
              <span data-testid="scm-sticky-coverage" style={{ fontSize: 15, fontWeight: 800, fontVariantNumeric: "tabular-nums",
                color: cov >= 95 ? "#34d399" : cov >= 70 ? "#fbbf24" : "#f87171" }}>{cov}%</span>
              <div style={{ flex: 1, height: 5, background: "rgba(255,255,255,0.08)", borderRadius: 6, overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 6, width: `${Math.min(100, cov)}%`,
                  background: cov >= 95 ? "#34d399" : cov >= 70 ? "#fbbf24" : "#f87171" }} />
              </div>
              <span data-testid="scm-sticky-scans" style={{ fontSize: 12, color: "#e2e8f0", whiteSpace: "nowrap" }}><b style={{ fontVariantNumeric: "tabular-nums" }}>{sm.included_scans || 0}</b> scanned</span>
              <span data-testid="scm-sticky-missing" style={{ fontSize: 11, color: "#a8a29e", whiteSpace: "nowrap" }}>{left} left</span>
              <span data-testid="scm-timer-value" style={{ fontSize: 11, fontVariantNumeric: "tabular-nums", color: isOpen ? "#34d399" : "#94a3b8", whiteSpace: "nowrap" }}>{fmtDuration(elapsedSecs)}</span>
              <button onClick={toggleSound} data-testid="scm-sound-toggle" aria-pressed={soundOn} title={soundOn ? "Scan sound on" : "Scan sound off"}
                style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, color: soundOn ? "#34d399" : "#78716c", lineHeight: 0 }}>
                {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
            </div>
            {/* Scan confirmation is shown as an overlay on the camera window (below) so it's visible while scanning */}

            {isOpen ? (
              <>
                {/* Zone chips (one horizontal-scroll row) + lock */}
                {zones.length === 0 ? (
                  <div style={{ fontSize: 11, color: "#a8a29e", marginBottom: 8 }} data-testid="scm-no-zones">
                    No zones set up yet. An admin can add zones in Administration.
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
                    <div data-testid="scm-zone-buttons" style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1, paddingBottom: 2 }}>
                      {zones.map((z) => (
                        <button key={z.id} onClick={() => selectZone(z.name)} data-testid={`scm-zone-${z.id}`}
                          style={{ whiteSpace: "nowrap", padding: "7px 13px", borderRadius: 999, fontSize: 13, fontWeight: 600, border: "1px solid",
                            borderColor: zone === z.name ? "#fbbf24" : "rgba(255,255,255,0.14)",
                            background: zone === z.name ? "rgba(251,191,36,0.18)" : "transparent",
                            color: zone === z.name ? "#fbbf24" : "#a8a29e" }}>
                          {z.name}
                        </button>
                      ))}
                    </div>
                    <button onClick={toggleZoneLock} data-testid="scm-zone-lock-toggle" aria-pressed={zoneLocked}
                      title={zoneLocked ? "Zone locked — tap to unlock" : "Lock this zone"}
                      style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4, padding: "7px 10px",
                        borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "1px solid",
                        borderColor: zoneLocked ? "#fbbf24" : "rgba(255,255,255,0.14)",
                        background: zoneLocked ? "rgba(251,191,36,0.18)" : "transparent",
                        color: zoneLocked ? "#fbbf24" : "#a8a29e" }}>
                      {zoneLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                )}
                {!zone && (
                  <div data-testid="scm-sticky-nozone" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#f87171", marginBottom: 8 }}>
                    <AlertTriangle className="w-3.5 h-3.5" /> Select a zone to start counting
                  </div>
                )}
                {/* 1) Camera scanner — always live */}
                <div style={{ position: "relative", marginTop: 4 }}>
                  <Scanner ref={scannerRef} key={`sc-${scanNonce}`} onDecoded={onScan} hint="Aim at the pallet sticker (PLT-…)" />
                  {zone && (
                    <div data-testid="scm-cam-zone-progress" style={{
                      position: "absolute", top: 52, left: 8, zIndex: 7,
                      display: "flex", alignItems: "center", gap: 6,
                      background: "rgba(12,15,20,0.82)", border: "1px solid rgba(251,191,36,0.4)",
                      borderRadius: 999, padding: "5px 12px", backdropFilter: "blur(2px)" }}>
                      <span style={{ width: 7, height: 7, borderRadius: 999, background: "#fbbf24", display: "inline-block" }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#fbbf24" }}>{zone}</span>
                      <span style={{ fontSize: 12, color: "#e2e8f0" }}>· {zoneCounted} done</span>
                      {zoneSkips > 0 && <span style={{ fontSize: 12, color: "#38bdf8", fontWeight: 700 }}>· {zoneSkips} skip</span>}
                    </div>
                  )}
                  {isOpen && (sm.total_unidentified || 0) > 0 && (
                    <button onClick={() => setSheet("counts")} data-testid="scm-cam-unident-chip"
                      style={{ position: "absolute", top: 52, right: 8, zIndex: 7,
                        display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
                        background: "rgba(12,15,20,0.82)", border: "1px solid rgba(56,189,248,0.5)",
                        borderRadius: 999, padding: "5px 12px", backdropFilter: "blur(2px)" }}>
                      <Camera className="w-3 h-3" style={{ color: "#38bdf8" }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#38bdf8" }}>{sm.total_unidentified} unidentified</span>
                    </button>
                  )}
                  {isOpen && (
                    <button onClick={snapPhoto} data-testid="scm-shutter-btn"
                      title="Photograph an unidentified item"
                      style={{ position: "absolute", left: "50%", bottom: 12, transform: "translateX(-50%)", zIndex: 5,
                        width: 60, height: 60, borderRadius: 999, cursor: "pointer",
                        background: "rgba(255,255,255,0.95)", border: "4px solid rgba(255,255,255,0.55)",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Camera className="w-6 h-6" style={{ color: "#1c1917" }} />
                    </button>
                  )}
                  {lastScan && (
                    <div data-testid="scm-last-scan" style={{
                      position: "absolute", left: 8, right: 8, bottom: 8, zIndex: 6,
                      borderRadius: 14, padding: "12px 14px", textAlign: "center",
                      background: lastScan.dup ? "rgba(146,97,10,0.95)" : lastScan.included ? "rgba(6,78,59,0.95)" : "rgba(127,29,29,0.95)",
                      border: `2px solid ${lastScan.dup ? "#fbbf24" : lastScan.included ? "#34d399" : "#f87171"}`,
                      boxShadow: "0 8px 28px rgba(0,0,0,0.55)", animation: "scmPulse 0.25s ease-out",
                    }}>
                      {lastScan.dup ? (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                          <AlertTriangle className="w-6 h-6" style={{ color: "#fde68a", flexShrink: 0 }} />
                          <div style={{ textAlign: "left" }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color: "#fde68a" }}>Already scanned</div>
                            <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "rgba(255,255,255,0.85)" }}>{lastScan.pid}</div>
                          </div>
                        </div>
                      ) : lastScan.included ? (
                        <>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                            <CheckCircle2 className="w-7 h-7" style={{ color: "#6ee7b7", flexShrink: 0 }} />
                            <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 24, fontWeight: 900, color: "#fff", lineHeight: 1.1, wordBreak: "break-all" }} data-testid="scm-last-scan-code">{lastScan.product_code}</div>
                          </div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: "#6ee7b7", marginTop: 2 }}>+{lastScan.qty} pcs{lastScan.pallet_id ? ` · #${String(lastScan.pallet_id).slice(-3)}` : ""}</div>
                        </>
                      ) : (
                        <>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                            <XCircle className="w-6 h-6" style={{ color: "#fca5a5", flexShrink: 0 }} />
                            <div style={{ fontSize: 16, fontWeight: 800, color: "#fca5a5" }}>Excluded</div>
                          </div>
                          <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 14, fontWeight: 700, color: "#fff", marginTop: 2 }}>{lastScan.product_code || lastScan.pallet_id}</div>
                          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>{lastScan.reason}</div>
                        </>
                      )}
                      {isOpen && !lastScan.dup && (
                        <button onClick={undoLast} disabled={busy} data-testid="scm-undo-last"
                          style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6,
                            padding: "6px 16px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                            border: "1px solid rgba(255,255,255,0.3)", background: "rgba(0,0,0,0.3)", color: "#fff", cursor: "pointer" }}>
                          <Undo2 className="w-3.5 h-3.5" /> Undo
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Captured photos of an unidentified item — snap more, then save as one item */}
                {photos.length > 0 && (
                  <div data-testid="scm-photo-preview" style={{ marginTop: 10,
                    background: "rgba(56,189,248,0.08)", border: "1px solid rgba(56,189,248,0.3)", borderRadius: 12, padding: 10 }}>
                    <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 8 }}>
                      {photos.map((p, i) => (
                        <div key={i} style={{ position: "relative", flexShrink: 0 }}>
                          <img src={p.url} alt="unidentified item" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, display: "block" }} />
                          <button onClick={() => removePhoto(i)} data-testid={`scm-photo-remove-${i}`}
                            style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: 999, border: "none",
                              background: "#ef4444", color: "#fff", fontSize: 11, lineHeight: 1, cursor: "pointer" }}>✕</button>
                        </div>
                      ))}
                      <div style={{ flexShrink: 0, width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, textAlign: "center", color: "#38bdf8", border: "1px dashed rgba(56,189,248,0.4)", borderRadius: 8 }}>
                        Tap ◉ to add
                      </div>
                    </div>
                    <input className="m-input" value={photoNote} onChange={(e) => setPhotoNote(e.target.value)}
                      placeholder="Location hint (aisle / rack / bay)" data-testid="scm-photo-note" style={{ marginBottom: 8, fontSize: 12 }} />
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={discardPhotos} disabled={busy} data-testid="scm-photo-retake"
                        style={{ flex: 1, padding: "9px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                          border: "1px solid rgba(255,255,255,0.16)", background: "transparent", color: "#e2e8f0" }}>Discard</button>
                      <button onClick={savePhoto} disabled={busy} data-testid="scm-photo-save"
                        style={{ flex: 2, padding: "9px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                          border: "none", background: "#34d399", color: "#052e26" }}>Save item ({photos.length} photo{photos.length > 1 ? "s" : ""})</button>
                    </div>
                  </div>
                )}

                {/* Compact skip row (per zone) */}
                {zone && (
                  <div data-testid="scm-skip-card" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, marginBottom: 8 }}>
                    <button onClick={resolveSkip} disabled={busy || zoneSkips <= 0} data-testid="scm-skip-minus"
                      style={{ width: 38, height: 38, borderRadius: 999, fontSize: 20, fontWeight: 800, flexShrink: 0, cursor: zoneSkips > 0 ? "pointer" : "not-allowed",
                        border: "1px solid rgba(255,255,255,0.18)", background: "transparent", color: zoneSkips > 0 ? "#e2e8f0" : "#57534e" }}>−</button>
                    <div style={{ fontSize: 13, color: "#a8a29e", whiteSpace: "nowrap" }}>Skipped <b data-testid="scm-skip-count" style={{ color: "#38bdf8", fontSize: 18, fontVariantNumeric: "tabular-nums" }}>{zoneSkips}</b></div>
                    <button onClick={addSkip} disabled={busy} data-testid="scm-skip-plus"
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "9px 14px", borderRadius: 999, fontSize: 14, fontWeight: 800, flexShrink: 0, cursor: "pointer",
                        border: "none", background: "#38bdf8", color: "#0c1a24" }}>
                      <Plus className="w-4 h-4" /> Skip
                    </button>
                    <button onClick={() => setResolveMode((v) => !v)} data-testid="scm-resolve-toggle" aria-pressed={resolveMode}
                      title={resolveMode ? "Resolve mode ON — each add clears a skip" : "Resolve mode OFF — tap to auto-clear skips as you add"}
                      style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, padding: "9px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, flexShrink: 0, cursor: "pointer", border: "1px solid",
                        borderColor: resolveMode ? "#34d399" : "rgba(255,255,255,0.16)",
                        background: resolveMode ? "rgba(52,211,153,0.2)" : "transparent",
                        color: resolveMode ? "#34d399" : "#a8a29e" }}>
                      {resolveMode ? <CheckCircle2 className="w-4 h-4" /> : <Undo2 className="w-4 h-4" />}
                      Resolve
                    </button>
                  </div>
                )}

                {/* Action row — secondary tools open as bottom-sheets, so the scan screen never scrolls */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginTop: 4 }}>
                  <button onClick={() => setSheet("type")} data-testid="scm-open-type" style={scmActionBtn}>
                    <Keyboard className="w-4 h-4" /> Type ID
                  </button>
                  <button onClick={() => setSheet("noqr")} data-testid="scm-open-noqr" style={scmActionBtn}>
                    <Package className="w-4 h-4" /> No QR
                  </button>
                  <button onClick={() => setSheet("counts")} data-testid="scm-open-counts" style={scmActionBtn}>
                    <BarChart3 className="w-4 h-4" /> Counts
                  </button>
                  <button onClick={() => setShowFinalize(true)} disabled={busy} data-testid="scm-finalize-btn"
                    style={{ ...scmActionBtn, background: "#f97316", borderColor: "#f97316", color: "#fff" }}>
                    <FileSpreadsheet className="w-4 h-4" /> Finalize
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: "#a8a29e", fontSize: 13, textAlign: "center", marginTop: 20 }}>
                Session closed. Open the reconciliation report on desktop to view the drill-down.
                <button onClick={() => setSheet("counts")} data-testid="scm-open-counts" style={{ ...scmActionBtn, flexDirection: "row", fontSize: 13, marginTop: 14, width: "100%" }}>
                  <BarChart3 className="w-4 h-4" /> View counts & export
                </button>
              </div>
            )}

            {sheet === "counts" && (
              <SCSheet title="Counts & export" onClose={() => setSheet(null)} testId="scm-counts-sheet">
            {(active.scans || []).length > 0 && (
              <button onClick={exportCsv} data-testid="scm-export-csv-btn"
                style={{ marginBottom: 14, width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                  padding: "10px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer",
                  border: "1px solid rgba(255,255,255,0.14)", background: "transparent", color: "#e2e8f0" }}>
                <Download className="w-4 h-4" /> Export scan log (CSV)
              </button>
            )}
            <div className="m-card-label" style={{ margin: "0 0 8px" }}>Counted by product</div>
            {(active.per_product || []).length === 0 && <div style={{ color: "#a8a29e", fontSize: 13 }}>Nothing counted yet.</div>}
            {(active.per_product || []).map((r) => (
              <div key={r.product_code} data-testid={`scm-prod-${r.product_code}`}
                style={{ display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "8px 10px", marginBottom: 6, fontSize: 13 }}>
                <span style={{ fontFamily: "ui-monospace, monospace" }}>{r.product_code}</span>
                <span><b>{r.physical_pieces}</b> pcs · {r.pallets} pal{r.manual_pieces ? ` · ${r.manual_pieces} loose` : ""}</span>
              </div>
            ))}

            {/* By zone — pallets & pieces counted in each zone (tap a row to filter the scan log) */}
            {(active.per_zone || []).length > 0 && (
              <>
                <div className="m-card-label" style={{ margin: "16px 0 8px" }}>Counted by zone <span style={{ color: "#78716c", fontWeight: 400 }}>(tap to filter)</span></div>
                {(active.per_zone || []).map((z) => {
                  const activeChip = scanZone === z.zone;
                  return (
                  <button key={z.zone} data-testid={`scm-zone-row-${z.zone}`}
                    onClick={() => setScanZone(activeChip ? "" : z.zone)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", textAlign: "left", cursor: "pointer",
                      background: activeChip ? "rgba(251,191,36,0.18)" : "rgba(251,191,36,0.06)",
                      border: `1px solid ${activeChip ? "#fbbf24" : "rgba(251,191,36,0.20)"}`, borderRadius: 8, padding: "8px 10px", marginBottom: 6, fontSize: 13 }}>
                    <span style={{ fontWeight: 700, color: "#fbbf24" }}>{z.zone}{activeChip ? " ✓" : ""}</span>
                    <span style={{ color: "#e2e8f0" }}><b>{z.pieces}</b> pcs · {z.pallets} pal{z.loose ? ` · ${z.loose} loose` : ""}{z.skipped ? <span style={{ color: "#38bdf8", fontWeight: 700 }}> · {z.skipped} skipped</span> : ""}</span>
                  </button>
                  );
                })}
              </>
            )}

            {/* Scanned pallets — product code prominent; matched pallet's last 3 digits alongside */}
            {(active.unidentified || []).length > 0 && (
              <>
                <div className="m-card-label" style={{ margin: "16px 0 8px" }}>Unidentified items ({(active.unidentified || []).filter((u) => !u.resolved).length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 6 }}>
                  {(active.unidentified || []).map((u) => {
                    const pics = u.photos && u.photos.length ? u.photos : (u.file_id ? [u.file_id] : []);
                    const after = u.after_scan ? (u.after_scan.product_code || u.after_scan.pallet_id) : null;
                    const before = u.before_scan ? (u.before_scan.product_code || u.before_scan.pallet_id) : null;
                    const ri = uResolve[u.id] || {};
                    const rq = (ri.q ?? ri.code ?? "").toUpperCase();
                    const matches = (rq && !ri.code) ? codes.filter((c) => c.toUpperCase().includes(rq)).slice(0, 6) : [];
                    return (
                      <div key={u.id} data-testid={`scm-unident-${u.id}`}
                        style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(56,189,248,0.06)",
                          border: "1px solid rgba(56,189,248,0.25)", borderRadius: 10, padding: 8 }}>
                        <button type="button" onClick={() => setLightbox({ pics, i: 0 })} data-testid={`scm-unident-photo-${u.id}`}
                          style={{ position: "relative", flexShrink: 0, padding: 0, border: "none", background: "none", cursor: "pointer" }}>
                          <img src={api.scPhotoUrl(pics[0])} alt="unidentified item" loading="lazy"
                            style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, display: "block" }} />
                          <span style={{ position: "absolute", top: 3, left: 3, background: "rgba(0,0,0,0.6)", color: "#fff",
                            fontSize: 9, fontWeight: 700, borderRadius: 6, padding: "2px 5px", display: "inline-flex", alignItems: "center", gap: 2 }}>
                            <Maximize2 style={{ width: 9, height: 9 }} /> tap
                          </span>
                          {pics.length > 1 && (
                            <span style={{ position: "absolute", bottom: 2, right: 2, background: "rgba(0,0,0,0.7)", color: "#fff",
                              fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "1px 5px" }}>+{pics.length - 1}</span>
                          )}
                        </button>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#fbbf24" }}>{u.zone} · #{u.zone_seq || 0}</div>
                          <div style={{ fontSize: 11, color: "#e2e8f0", marginTop: 2 }} data-testid={`scm-unident-anchor-${u.id}`}>
                            {after ? <>after <b style={{ fontFamily: "ui-monospace, monospace" }}>{after}</b></> : "start of zone"}
                            {before ? <> → before <b style={{ fontFamily: "ui-monospace, monospace" }}>{before}</b></> : " (last so far)"}
                          </div>
                          {u.note && (
                            <div style={{ fontSize: 11, color: "#a8a29e", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                              <MapPin className="w-3 h-3" /> {u.note}
                            </div>
                          )}
                          {u.resolved ? (
                            (u.resolution_type === "note" || (u.resolved_note && !u.resolved_product_code)) ? (
                              <div data-testid={`scm-unident-resolved-${u.id}`} style={{ display: "inline-flex", alignItems: "flex-start", gap: 4, marginTop: 6, fontSize: 11, fontWeight: 700, color: "#a8a29e", background: "rgba(168,162,158,0.12)", border: "1px solid rgba(168,162,158,0.3)", borderRadius: 8, padding: "3px 8px" }}>
                                <StickyNote className="w-3 h-3" style={{ flexShrink: 0, marginTop: 1 }} /> Noted (not counted): {u.resolved_note}
                              </div>
                            ) : (
                              <div data-testid={`scm-unident-resolved-${u.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6, fontSize: 11, fontWeight: 700, color: "#34d399", background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.3)", borderRadius: 999, padding: "3px 8px" }}>
                                <CheckCircle2 className="w-3 h-3" /> {u.resolved_product_code} · {u.resolved_qty} pcs{u.resolved_pallet_id ? ` · ${u.resolved_pallet_id}` : (u.resolved_pallets ? ` (${u.resolved_pallets}×${u.resolved_pcs_per_pallet})` : "")}
                              </div>
                            )
                          ) : (
                            <div style={{ marginTop: 8 }}>
                              {!isOpen && <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>Finalized — resolving updates the report.</div>}
                              <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                                <button onClick={() => setUr(u.id, { mode: "product" })} data-testid={`scm-unident-mode-product-${u.id}`}
                                  style={{ flex: 1, padding: "6px 4px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "1px solid", borderColor: (ri.mode || "product") === "product" ? "#34d399" : "rgba(255,255,255,0.16)", background: (ri.mode || "product") === "product" ? "rgba(52,211,153,0.15)" : "transparent", color: (ri.mode || "product") === "product" ? "#34d399" : "#a8a29e" }}>Product</button>
                                <button onClick={() => setUr(u.id, { mode: "pallet" })} data-testid={`scm-unident-mode-pallet-${u.id}`}
                                  style={{ flex: 1, padding: "6px 4px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "1px solid", borderColor: ri.mode === "pallet" ? "#38bdf8" : "rgba(255,255,255,0.16)", background: ri.mode === "pallet" ? "rgba(56,189,248,0.15)" : "transparent", color: ri.mode === "pallet" ? "#38bdf8" : "#a8a29e" }}>Pallet ID</button>
                                <button onClick={() => setUr(u.id, { mode: "note" })} data-testid={`scm-unident-mode-note-${u.id}`}
                                  style={{ flex: 1, padding: "6px 4px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "1px solid", borderColor: ri.mode === "note" ? "#fbbf24" : "rgba(255,255,255,0.16)", background: ri.mode === "note" ? "rgba(251,191,36,0.15)" : "transparent", color: ri.mode === "note" ? "#fbbf24" : "#a8a29e" }}>Note</button>
                              </div>
                              {(ri.mode || "product") === "product" && (<>
                              <input className="m-input" value={ri.q ?? ri.code ?? ""} onChange={(e) => setUr(u.id, { q: e.target.value.toUpperCase(), code: "" })} placeholder="Identify product code" data-testid={`scm-unident-code-${u.id}`} style={{ marginBottom: 6, padding: "8px", fontSize: 13 }} />
                              {matches.length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                                  {matches.map((c) => (
                                    <button key={c} onClick={() => pickResolveCode(u.id, c)} data-testid={`scm-unident-codeopt-${u.id}-${c}`}
                                      style={{ padding: "5px 8px", borderRadius: 999, fontSize: 12, fontFamily: "ui-monospace, monospace", cursor: "pointer",
                                        border: "1px solid rgba(251,191,36,0.3)", background: ri.code === c ? "rgba(251,191,36,0.25)" : "rgba(251,191,36,0.06)", color: "#fbbf24" }}>{c}</button>
                                  ))}
                                </div>
                              )}
                              {/* Count by pallets (fetches pcs/pallet) — like the no-QR option */}
                              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                                <input className="m-input" type="number" inputMode="numeric" min="1" value={ri.pallets || ""} onChange={(e) => setUr(u.id, { pallets: e.target.value })} placeholder="No. of pallets" data-testid={`scm-unident-pallets-${u.id}`} style={{ flex: 1, padding: "8px", fontSize: 13 }} disabled={!ri.code} />
                                <span style={{ color: "#78716c", fontSize: 13 }}>×</span>
                                <input className="m-input" type="number" inputMode="numeric" min="1" value={ri.pcsPer || ""} onChange={(e) => setUr(u.id, { pcsPer: e.target.value })} placeholder="Pcs / pallet" data-testid={`scm-unident-pcsper-${u.id}`} style={{ flex: 1, padding: "8px", fontSize: 13 }} disabled={!ri.code} />
                              </div>
                              {Number(ri.pallets) > 0 && Number(ri.pcsPer) > 0 && (
                                <div style={{ fontSize: 12, color: "#34d399", fontWeight: 700, marginBottom: 6 }} data-testid={`scm-unident-total-${u.id}`}>
                                  = {Number(ri.pallets) * Number(ri.pcsPer)} pcs total
                                </div>
                              )}
                              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <input className="m-input" type="number" inputMode="numeric" value={ri.qty || ""} onChange={(e) => setUr(u.id, { qty: e.target.value })} placeholder={Number(ri.pallets) > 0 ? "Using pallets ×" : "Or exact qty (pcs)"} data-testid={`scm-unident-qty-${u.id}`} style={{ flex: 1, padding: "8px", fontSize: 13, opacity: Number(ri.pallets) > 0 ? 0.5 : 1 }} disabled={!ri.code || Number(ri.pallets) > 0} />
                                <button onClick={() => resolveUnident(u.id)} disabled={busy} data-testid={`scm-unident-resolve-${u.id}`}
                                  style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4, padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "none", background: "#059669", color: "#fff" }}>
                                  <CheckCircle2 className="w-4 h-4" /> Resolve
                                </button>
                              </div>
                              </>)}
                              {ri.mode === "pallet" && (
                                <div>
                                  <input className="m-input" inputMode="numeric" value={ri.shortId || ""} onChange={(e) => setUr(u.id, { shortId: e.target.value, palletCandidates: null })} onKeyDown={(e) => { if (e.key === "Enter") resolvePallet(u.id); }} placeholder="Pallet short number (e.g. 042)" data-testid={`scm-unident-shortid-${u.id}`} style={{ width: "100%", marginBottom: 6, padding: "8px", fontSize: 13 }} />
                                  {(ri.palletCandidates || []).length > 0 && (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 6 }} data-testid={`scm-unident-pallet-cands-${u.id}`}>
                                      <div style={{ fontSize: 10, color: "#a8a29e" }}>Multiple match — pick the right pallet:</div>
                                      {ri.palletCandidates.map((c) => (
                                        <button key={c.id} onClick={() => resolvePallet(u.id, c.id)} disabled={busy} data-testid={`scm-unident-pallet-cand-${u.id}-${c.id}`}
                                          style={{ textAlign: "left", padding: "6px 8px", borderRadius: 8, fontSize: 12, cursor: "pointer", border: "1px solid rgba(56,189,248,0.3)", background: "rgba(56,189,248,0.08)", color: "#e0f2fe" }}>
                                          <span style={{ fontFamily: "ui-monospace, monospace" }}>{c.pallet_id}</span> · {c.product_code} · {c.qty} pcs · {c.state}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  <div style={{ fontSize: 10, color: "#a8a29e", marginBottom: 6 }}>Links to the real pallet — product & qty come from it, counted like a scan.</div>
                                  <button onClick={() => resolvePallet(u.id)} disabled={busy} data-testid={`scm-unident-resolvepallet-${u.id}`}
                                    style={{ width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "none", background: "#0284c7", color: "#fff" }}>
                                    <CheckCircle2 className="w-4 h-4" /> Find & link pallet
                                  </button>
                                </div>
                              )}
                              {ri.mode === "note" && (
                                <div>
                                  <textarea className="m-input" value={ri.noteText || ""} onChange={(e) => setUr(u.id, { noteText: e.target.value })} placeholder="e.g. Empty pallet · scrap · not our product · needs follow-up" data-testid={`scm-unident-notetext-${u.id}`} rows={2} style={{ width: "100%", marginBottom: 6, padding: "8px", fontSize: 13, resize: "vertical" }} />
                                  <div style={{ fontSize: 10, color: "#a8a29e", marginBottom: 6 }}>Closes this item with a note — nothing is added to the count.</div>
                                  <button onClick={() => resolveNote(u.id)} disabled={busy} data-testid={`scm-unident-savenote-${u.id}`}
                                    style={{ width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "none", background: "#d97706", color: "#fff" }}>
                                    <StickyNote className="w-4 h-4" /> Save note & close
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {canManage && !u.resolved && (
                          <button onClick={() => deleteUnident(u.id)} data-testid={`scm-unident-del-${u.id}`}
                            style={{ flexShrink: 0, width: 24, height: 24, borderRadius: 999, border: "none",
                              background: "#ef4444", color: "#fff", fontSize: 12, lineHeight: 1, cursor: "pointer" }}>✕</button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: "#38bdf8", marginBottom: 6 }}>Identify items here or on desktop — resolved items roll into the count as keyed entries.</div>
              </>
            )}

            {/* Scanned pallets — product code prominent; matched pallet's last 3 digits alongside */}
            <div className="m-card-label" style={{ margin: "16px 0 8px" }}>
              Scanned pallets {(active.scans || []).length ? `(${filteredScans.length}/${(active.scans || []).length})` : ""}
              {scanZone && <span style={{ color: "#78716c", fontWeight: 400 }}> · walk # = order scanned in {scanZone}</span>}
            </div>

            {(active.scans || []).length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <input
                  className="m-input"
                  value={scanQuery}
                  onChange={(e) => setScanQuery(e.target.value)}
                  placeholder="Search product code or pallet ID"
                  data-testid="scm-scan-search"
                  style={{ marginBottom: 8 }}
                />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => setScanExcludedOnly((v) => !v)} data-testid="scm-scan-excluded-toggle"
                    style={{ padding: "6px 10px", borderRadius: 999, fontSize: 12, border: "1px solid",
                      borderColor: scanExcludedOnly ? "#f87171" : "rgba(255,255,255,0.14)",
                      background: scanExcludedOnly ? "rgba(248,113,113,0.18)" : "transparent",
                      color: scanExcludedOnly ? "#f87171" : "#a8a29e" }}>
                    Excluded only
                  </button>
                  {zonesList.length > 0 && (
                    <button onClick={() => setScanZone("")} data-testid="scm-scan-zone-all"
                      style={{ padding: "6px 10px", borderRadius: 999, fontSize: 12, border: "1px solid",
                        borderColor: scanZone === "" ? "#f97316" : "rgba(255,255,255,0.14)",
                        background: scanZone === "" ? "rgba(249,115,22,0.18)" : "transparent",
                        color: scanZone === "" ? "#f97316" : "#a8a29e" }}>
                      All zones
                    </button>
                  )}
                  {zonesList.map((z) => (
                    <button key={z} onClick={() => setScanZone(z)} data-testid={`scm-scan-zone-${z}`}
                      style={{ padding: "6px 10px", borderRadius: 999, fontSize: 12, border: "1px solid",
                        borderColor: scanZone === z ? "#f97316" : "rgba(255,255,255,0.14)",
                        background: scanZone === z ? "rgba(249,115,22,0.18)" : "transparent",
                        color: scanZone === z ? "#f97316" : "#a8a29e" }}>
                      {z}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(active.scans || []).length === 0 && <div style={{ color: "#a8a29e", fontSize: 13 }}>No scans yet.</div>}
            {(active.scans || []).length > 0 && filteredScans.length === 0 && <div style={{ color: "#a8a29e", fontSize: 13 }}>No scans match the filter.</div>}
            {filteredScans.map((s) => (
              <div key={s.id} data-testid={`scm-scan-${s.id}`}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
                  background: s.included ? "rgba(255,255,255,0.04)" : "rgba(248,113,113,0.08)",
                  border: `1px solid ${s.included ? "rgba(255,255,255,0.06)" : "rgba(248,113,113,0.25)"}`,
                  borderRadius: 8, padding: "8px 10px", marginBottom: 6 }}>
                <div data-testid={`scm-scan-walk-${s.id}`} title={`Walk #${s.walk_no || "—"} in ${s.zone || "zone"}`}
                  style={{ flexShrink: 0, width: 34, textAlign: "center", fontSize: 12, fontWeight: 800,
                    fontVariantNumeric: "tabular-nums", color: "#fbbf24", background: "rgba(251,191,36,0.12)",
                    border: "1px solid rgba(251,191,36,0.25)", borderRadius: 6, padding: "3px 0" }}>
                  {s.walk_no || "—"}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontFamily: "ui-monospace, monospace", fontWeight: 800, fontSize: 16, color: s.included ? "#f1f5f9" : "#fca5a5" }} data-testid={`scm-scan-code-${s.id}`}>
                    {s.product_code || "—"}
                    {s.capture_method === "short_pick" && <span data-testid={`scm-scan-method-${s.id}`} style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: "#fbbf24", background: "rgba(251,191,36,0.14)", border: "1px solid rgba(251,191,36,0.4)", borderRadius: 4, padding: "1px 5px", verticalAlign: "middle" }}>PICKED BY #</span>}
                    {s.capture_method === "typed_id" && <span data-testid={`scm-scan-method-${s.id}`} style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: "#94a3b8", background: "rgba(148,163,184,0.12)", border: "1px solid rgba(148,163,184,0.35)", borderRadius: 4, padding: "1px 5px", verticalAlign: "middle" }}>TYPED ID</span>}
                    {s.capture_method === "qr" && <span data-testid={`scm-scan-method-${s.id}`} style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: "#34d399", background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.35)", borderRadius: 4, padding: "1px 5px", verticalAlign: "middle" }}>QR</span>}
                  </div>
                  {(s.zone || (!s.included && s.reason)) && (
                    <div style={{ color: "#a8a29e", fontSize: 11 }}>
                      {s.zone || ""}{s.zone && !s.included && s.reason ? " · " : ""}{!s.included && s.reason ? `excluded (${s.reason})` : ""}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "center", width: 52, color: "#78716c", fontSize: 12, fontFamily: "ui-monospace, monospace" }} data-testid={`scm-scan-pid-${s.id}`}>
                  {s.pallet_id ? `#${String(s.pallet_id).slice(-3)}` : (s.source === "manual" ? "keyed" : "—")}
                </div>
                <div style={{ textAlign: "right", whiteSpace: "nowrap", width: 60, fontWeight: 700, color: s.included ? "#34d399" : "#f87171" }}>
                  {s.qty} pcs
                </div>
              </div>
            ))}

            {canManage && (
              <button className="m-link-btn" onClick={() => deleteSession(active.session.id)} disabled={busy}
                data-testid="scm-delete-session-btn"
                style={{ marginTop: 16, color: "#f87171" }}>
                <Trash2 className="w-4 h-4 inline mr-1" /> Delete this count
              </button>
            )}
              </SCSheet>
            )}

            {sheet === "type" && (
              <SCSheet title="Type sticker number" onClose={() => setSheet(null)} testId="scm-type-sheet">
                <ManualEntry label="Full ID or short sticker number (e.g. 123)" value={manualInput} onChange={setManualInput} onSubmit={() => submitTypedId(manualInput)} />
                <div style={{ fontSize: 11, color: "#a8a29e", marginTop: 6 }}>
                  QR wrapped in plastic? Just type the short number printed on the pallet card.
                </div>
                {shortCandidates.length > 0 && (
                  <div data-testid="scm-short-candidates" style={{ marginTop: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(251,191,36,0.35)", borderRadius: 12, padding: 12 }}>
                    {shortCandidates.some((c) => c.legacy) && shortCandidates.some((c) => !c.legacy) && (
                      <div data-testid="scm-short-legacy-warning" style={{ fontSize: 12, fontWeight: 700, color: "#fca5a5", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>
                        ⚠ This number matches a real pallet AND a legacy pallet. Check the sticker carefully and pick the exact one you're holding.
                      </div>
                    )}
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#fbbf24", marginBottom: 8 }}>
                      {shortCandidates.length === 1
                        ? "Confirm the pallet before recording:"
                        : `${shortCandidates.length} pallets match — pick the right one:`}
                    </div>
                    {shortCandidates.map((c) => (
                      <button key={c.id} onClick={() => pickCandidate(c.id)} disabled={busy} data-testid={`scm-short-cand-${c.id}`}
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%",
                          background: "rgba(255,255,255,0.04)", border: c.legacy ? "1px solid rgba(239,68,68,0.4)" : "1px solid rgba(255,255,255,0.08)", borderRadius: 8,
                          padding: "8px 10px", marginBottom: 6, textAlign: "left", cursor: "pointer", color: "#e5e7eb" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontFamily: "ui-monospace, monospace", fontWeight: 800, fontSize: 15 }}>
                            {c.product_code || "—"}
                            {c.legacy && <span data-testid={`scm-short-cand-legacy-${c.id}`} style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#fca5a5", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 4, padding: "1px 5px", verticalAlign: "middle" }}>LEGACY</span>}
                          </div>
                          <div style={{ fontSize: 11, color: "#a8a29e" }}>{c.pallet_id}{c.po_no ? ` · PO ${c.po_no}` : ""} · {c.state}</div>
                        </div>
                        <div style={{ fontWeight: 700, color: "#34d399", whiteSpace: "nowrap", marginLeft: 8 }}>{c.qty} pcs</div>
                      </button>
                    ))}
                  </div>
                )}
              </SCSheet>
            )}

            {sheet === "noqr" && (
              <SCSheet title="No QR — count by product" onClose={() => setSheet(null)} testId="scm-noqr-sheet">
                <div style={{ fontSize: 12, color: "#a8a29e", marginBottom: 10 }}>For stacked or unreachable pallets — key the totals for the current zone.</div>
                <div data-testid="scm-noqr-form" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12 }}>
                  <input className="m-input" value={mProduct} onChange={(e) => setMProduct(e.target.value.toUpperCase())} onBlur={(e) => fetchHint(e.target.value)} placeholder="Search product code (e.g. GR-10)" data-testid="scm-m-product" style={{ marginBottom: 8 }} />
                  {mProduct.trim() && !codes.includes(mProduct.trim()) && (
                    <div data-testid="scm-m-code-list" style={{ maxHeight: 140, overflowY: "auto", marginBottom: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }}>
                      {codes.filter((c) => c.toUpperCase().includes(mProduct.trim())).slice(0, 20).map((c) => (
                        <button key={c} data-testid={`scm-m-code-${c}`} onClick={() => { setMProduct(c); setMPcsPer(""); fetchHint(c); }}
                          style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", background: "transparent",
                            border: "none", borderBottom: "1px solid rgba(255,255,255,0.05)", color: "#e5e7eb", fontFamily: "ui-monospace, monospace", fontSize: 13, cursor: "pointer" }}>
                          {c}
                        </button>
                      ))}
                      {codes.filter((c) => c.toUpperCase().includes(mProduct.trim())).length === 0 && (
                        <div style={{ padding: "8px 10px", color: "#a8a29e", fontSize: 12 }}>No matching code</div>
                      )}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    {["pallets", "pieces"].map((b) => (
                      <button key={b} onClick={() => setMBy(b)} data-testid={`scm-m-by-${b}`}
                        style={{ flex: 1, padding: "8px", borderRadius: 8, fontSize: 13, border: "1px solid",
                          borderColor: mBy === b ? "#f97316" : "rgba(255,255,255,0.12)",
                          background: mBy === b ? "#f97316" : "transparent", color: mBy === b ? "#fff" : "#a8a29e" }}>
                        By {b}
                      </button>
                    ))}
                  </div>
                  {mBy === "pallets" ? (
                    <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                      <input className="m-input" type="number" value={mPallets} onChange={(e) => setMPallets(e.target.value)} placeholder="# pallets" data-testid="scm-m-pallets" style={{ flex: 1 }} />
                      <span style={{ alignSelf: "center", color: "#a8a29e" }}>×</span>
                      <input className="m-input" type="number" value={mPcsPer} onChange={(e) => setMPcsPer(e.target.value)} placeholder="pcs/pallet" data-testid="scm-m-pcsper" style={{ flex: 1 }} />
                    </div>
                  ) : (
                    <input className="m-input" type="number" value={mQty} onChange={(e) => setMQty(e.target.value)} placeholder="Total pieces" data-testid="scm-m-qty" style={{ marginBottom: 8 }} />
                  )}
                  {mBy === "pallets" && Number(mPallets) > 0 && Number(mPcsPer) > 0 && (
                    <div style={{ fontSize: 12, color: "#34d399", marginBottom: 8 }} data-testid="scm-m-preview">= {Number(mPallets) * Number(mPcsPer)} pcs</div>
                  )}
                  <button className="m-btn" onClick={submitManual} disabled={busy} data-testid="scm-m-add">
                    <Plus className="w-4 h-4 inline mr-1" /> Add to count
                  </button>
                </div>
              </SCSheet>
            )}

            {showFinalize && (
              <div data-testid="scm-finalize-summary" onClick={() => !busy && setShowFinalize(false)}
                style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.6)",
                  display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                <div onClick={(e) => e.stopPropagation()}
                  style={{ width: "100%", maxWidth: 520, background: "#1c1917", borderTopLeftRadius: 18, borderTopRightRadius: 18,
                    borderTop: "1px solid rgba(255,255,255,0.1)", padding: 18, maxHeight: "82vh", overflowY: "auto" }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#f1f5f9", marginBottom: 4 }}>Finalize count?</div>
                  <div style={{ fontSize: 12, color: "#a8a29e", marginBottom: 14 }}>
                    Review what was counted per zone before generating the reconciliation.
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8, marginBottom: 14 }}>
                    <Stat label="Physical pcs" value={sm.physical_pieces} color="#34d399" testId="scm-fin-pieces" />
                    <Stat label="Coverage" value={`${sm.coverage_pct || 0}%`} color="#60a5fa" testId="scm-fin-coverage" />
                  </div>
                  <div className="m-card-label" style={{ marginBottom: 8 }}>By zone</div>
                  {(active.per_zone || []).length === 0 && (
                    <div style={{ color: "#a8a29e", fontSize: 13, marginBottom: 8 }}>Nothing counted yet.</div>
                  )}
                  {(active.per_zone || []).map((z) => (
                    <div key={z.zone} data-testid={`scm-fin-zone-${z.zone}`}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                        background: z.zone === "Unzoned" ? "rgba(248,113,113,0.08)" : "rgba(251,191,36,0.06)",
                        border: `1px solid ${z.zone === "Unzoned" ? "rgba(248,113,113,0.25)" : "rgba(251,191,36,0.20)"}`,
                        borderRadius: 8, padding: "8px 10px", marginBottom: 6, fontSize: 13 }}>
                      <span style={{ fontWeight: 700, color: z.zone === "Unzoned" ? "#f87171" : "#fbbf24" }}>{z.zone}</span>
                      <span style={{ color: "#e2e8f0" }}><b>{z.pieces}</b> pcs · {z.pallets} pal{z.loose ? ` · ${z.loose} loose` : ""}{z.skipped ? <span style={{ color: "#38bdf8", fontWeight: 700 }}> · {z.skipped} skipped</span> : ""}</span>
                    </div>
                  ))}
                  {totalSkips > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, marginBottom: 4 }} data-testid="scm-fin-skip-warning">
                      <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#38bdf8" }} />
                      <span style={{ fontSize: 11, color: "#38bdf8" }}>{totalSkips} pallet(s) still skipped (not yet counted).</span>
                    </div>
                  )}
                  {(sm.total_unidentified || 0) > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, marginBottom: 4 }} data-testid="scm-fin-unident-warning">
                      <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#38bdf8" }} />
                      <span style={{ fontSize: 11, color: "#38bdf8" }}>{sm.total_unidentified} unidentified item(s) pending — excluded from totals.</span>
                    </div>
                  )}
                  {(active.per_zone || []).some((z) => z.zone === "Unzoned") && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, marginBottom: 4 }}>
                      <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#f87171" }} />
                      <span style={{ fontSize: 11, color: "#f87171" }}>Some scans have no zone (Unzoned).</span>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                    <button onClick={() => setShowFinalize(false)} disabled={busy} data-testid="scm-fin-cancel"
                      style={{ flex: 1, padding: "12px", borderRadius: 10, fontSize: 14, fontWeight: 600,
                        border: "1px solid rgba(255,255,255,0.14)", background: "transparent", color: "#e2e8f0", cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button className="m-btn" onClick={finalize} disabled={busy} data-testid="scm-fin-confirm" style={{ flex: 1, marginTop: 0 }}>
                      <FileSpreadsheet className="w-4 h-4 inline mr-1" /> Finalize
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}


// ============================== Update Pallet (state transitions) ==============================

const STATE_BADGE = {
  PRINTED:     { color: "#94a3b8", bg: "rgba(148,163,184,0.15)", label: "Printed" },
  ASSIGNED:    { color: "#fbbf24", bg: "rgba(251,191,36,0.18)", label: "Assigned" },
  PRODUCED:    { color: "#34d399", bg: "rgba(52,211,153,0.18)", label: "Produced" },
  PACKED:      { color: "#60a5fa", bg: "rgba(96,165,250,0.18)", label: "Packed" },
  IN_CONTAINER:{ color: "#a78bfa", bg: "rgba(167,139,250,0.18)", label: "In Container" },
  DISPATCHED:  { color: "#f472b6", bg: "rgba(244,114,182,0.18)", label: "Dispatched" },
  DELIVERED:   { color: "#10b981", bg: "rgba(16,185,129,0.18)", label: "Delivered" },
  VOIDED:      { color: "#f87171", bg: "rgba(248,113,113,0.18)", label: "Voided" },
};

function StateBadge({ state }) {
  const s = STATE_BADGE[state] || { color: "#a8a29e", bg: "rgba(168,162,158,0.15)", label: state || "—" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: 999,
        background: s.bg,
        color: s.color,
        fontFamily: "Chivo",
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: 0.4,
        textTransform: "uppercase",
      }}
      data-testid={`pallet-state-${state}`}
    >
      {s.label}
    </span>
  );
}

function UpdatePallet({ user }) {
  const navigate = useNavigate();
  const { palletId: routeParamId } = useParams();
  const [step, setStep] = useState(routeParamId ? "loading" : "scan"); // scan | loading | detail
  const [pallet, setPallet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [scanNonce, setScanNonce] = useState(0);
  const [manualMode, setManualMode] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [unpackOpen, setUnpackOpen] = useState(false);
  const [unpackReason, setUnpackReason] = useState("");
  const [perms, setPerms] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignPos, setReassignPos] = useState([]);
  const [reassignPoId, setReassignPoId] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [reassignLoadingPos, setReassignLoadingPos] = useState(false);
  const updateScannerRef = useRef(null);
  const lastRescanTsRef = useRef(0);

  const canReassign = perms.includes("pallet.reassign_po");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.permissions();
        setPerms(r?.effective_permissions || []);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const rescan = () => {
    setErr(null);
    const now = Date.now();
    if (now - lastRescanTsRef.current < 2000) {
      lastRescanTsRef.current = 0;
      setScanNonce((n) => n + 1);
      return;
    }
    lastRescanTsRef.current = now;
    if (updateScannerRef.current?.resume) updateScannerRef.current.resume();
    else setScanNonce((n) => n + 1);
  };

  // If we landed via /mobile/update/:palletId, pre-load it
  useEffect(() => {
    if (!routeParamId) return;
    (async () => {
      setBusy(true);
      try {
        const p = await api.getPallet(routeParamId);
        setPallet(p);
        setStep("detail");
      } catch (ex) {
        setErr(ex.response?.data?.detail || "Pallet not found");
        setStep("scan");
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParamId]);

  const loadPallet = async (id) => {
    const raw = String(id || "").trim().toUpperCase();
    if (!isPalletId(raw)) {
      setErr(`"${raw}" is not a pallet QR. Expected format PLT-YYYY-NNNNNN.`);
      updateScannerRef.current?.resume?.();
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const p = await api.getPallet(raw);
      setPallet(p);
      setStep("detail");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Pallet not found");
      updateScannerRef.current?.resume?.();
    } finally {
      setBusy(false);
    }
  };

  const markProduced = async () => {
    if (!pallet) return;
    setBusy(true);
    setErr(null);
    try {
      const p = await api.produce(pallet.id, null, null);
      setPallet(p);
      toast.success(`${p.id} produced · ${p.actual_qty} pcs`);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not mark Produced");
    } finally {
      setBusy(false);
    }
  };

  const submitVoid = async () => {
    if (!pallet || !voidReason.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const p = await api.voidPallet(pallet.id, voidReason.trim());
      setPallet(p);
      setVoidOpen(false);
      setVoidReason("");
      toast.success(`${p.id} voided`);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not void pallet");
    } finally {
      setBusy(false);
    }
  };

  const markPacked = async () => {
    if (!pallet) return;
    setBusy(true);
    setErr(null);
    try {
      const p = await api.packPallet(pallet.id, DEVICE_ID);
      setPallet(p);
      toast.success(`${p.id} packed · contents sealed to pallet`);
      // Auto-download A4 card so operator can grab the printout
      try {
        const blob = await api.cardPdfBlob(p.id);
        const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        window.open(url, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch {
        /* non-fatal */
      }
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not pack pallet");
    } finally {
      setBusy(false);
    }
  };

  const submitUnpack = async () => {
    if (!pallet || !unpackReason.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const p = await api.unpackPallet(pallet.id, unpackReason.trim());
      setPallet(p);
      setUnpackOpen(false);
      setUnpackReason("");
      toast.success(`${p.id} unpacked — back to Produced`);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not unpack pallet");
    } finally {
      setBusy(false);
    }
  };

  const openReassign = async () => {
    setReassignOpen(true);
    setReassignPoId(pallet?.po_id || "");
    setReassignReason("");
    setReassignPos([]);
    if (pallet?.product_code) {
      setReassignLoadingPos(true);
      try {
        const list = await api.poPicker(pallet.product_code);
        setReassignPos(list || []);
      } catch {
        /* operator can still unassign */
      } finally {
        setReassignLoadingPos(false);
      }
    }
  };

  const submitReassign = async () => {
    if (!pallet || !reassignReason.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const p = await api.reassignPo(pallet.id, reassignPoId || null, reassignReason.trim());
      setPallet(p);
      setReassignOpen(false);
      setReassignReason("");
      toast.success(reassignPoId ? `Reassigned to PO ${p.po_no}` : `${p.id} unassigned`);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not change PO");
    } finally {
      setBusy(false);
    }
  };

  const downloadCard = async () => {
    if (!pallet) return;
    try {
      const blob = await api.cardPdfBlob(pallet.id);
      const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Could not load card");
    }
  };

  const scanAnother = () => {
    setStep("scan");
    setPallet(null);
    setErr(null);
    setVoidOpen(false);
    setVoidReason("");
    setReassignOpen(false);
    setReassignReason("");
    setManualMode(false);
    setManualInput("");
  };

  return (
    <>
      <SimpleChrome title="Update Pallet" onBack={() => navigate("/mobile")} />
      <div className="m-screen">
        {err && <div className="m-error-banner" data-testid="update-error">{err}</div>}

        {step === "loading" && (
          <div style={{ textAlign: "center", paddingTop: 40 }}>
            <Loader2 className="animate-spin mx-auto" />
            <div style={{ color: "#a8a29e", fontSize: 13, marginTop: 8 }}>Loading pallet…</div>
          </div>
        )}

        {step === "scan" && (
          <>
            <h3 style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 18, margin: "6px 0 10px" }}>
              Scan a PALLET QR
            </h3>
            <p style={{ color: "#a8a29e", fontSize: 13, marginBottom: 10 }}>
              Look up any pallet to advance its state, void it, or just check what it is.
            </p>
            {!manualMode ? (
              <Scanner
                ref={updateScannerRef}
                key={`update-${scanNonce}`}
                onDecoded={loadPallet}
                hint="Aim at the pallet sticker (PLT-…)"
              />
            ) : (
              <ManualEntry
                label="Type pallet ID (e.g. PLT-2026-000123)"
                value={manualInput}
                onChange={setManualInput}
                onSubmit={() => loadPallet(manualInput)}
              />
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 8 }}>
              {!manualMode && (
                <button className="m-link-btn" onClick={rescan} data-testid="rescan-update">
                  ↻ Rescan
                </button>
              )}
              <button
                className="m-link-btn"
                onClick={() => setManualMode((v) => !v)}
                data-testid="toggle-manual-update"
              >
                <Keyboard className="w-3 h-3 inline mr-1" />
                {manualMode ? "Use camera" : "Type ID instead"}
              </button>
            </div>
          </>
        )}

        {step === "detail" && pallet && (
          <>
            <div className="m-card" data-testid="update-pallet-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="m-card-mono" style={{ fontSize: 18 }}>{pallet.id}</div>
                <StateBadge state={pallet.state} />
              </div>
              <div className="m-divider" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div className="m-card-label">Product</div>
                  <div className="m-card-value">{pallet.product_code || "—"}</div>
                </div>
                <div>
                  <div className="m-card-label">PO</div>
                  <div className="m-card-mono" style={{ fontSize: 14 }}>{pallet.po_no || "Unassigned"}</div>
                </div>
                <div>
                  <div className="m-card-label">Target Qty</div>
                  <div className="m-card-value">{pallet.target_qty ?? "—"}</div>
                </div>
                <div>
                  <div className="m-card-label">Actual Qty</div>
                  <div className="m-card-value">{pallet.actual_qty ?? "—"}</div>
                </div>
              </div>
              {pallet.customer_name && (
                <>
                  <div className="m-divider" />
                  <div className="m-card-label">Customer</div>
                  <div className="m-card-value">{pallet.customer_name}</div>
                </>
              )}
            </div>

            {/* Change PO — available for reassignable states */}
            {canReassign &&
              ["ASSIGNED", "PRODUCED", "PACKED"].includes(pallet.state) &&
              !reassignOpen && !voidOpen && !unpackOpen && (
                <button
                  className="m-btn m-btn--ghost"
                  style={{ marginTop: 12 }}
                  onClick={openReassign}
                  data-testid="update-open-reassign"
                >
                  <ArrowLeftRight className="w-4 h-4 inline mr-1" />
                  {pallet.po_no ? "Change PO" : "Assign to PO"}
                </button>
              )}

            {reassignOpen && (
              <div className="m-card" style={{ marginTop: 12 }} data-testid="reassign-form">
                <div className="m-card-label" style={{ marginBottom: 8 }}>
                  Change PO for {pallet.product_code}
                </div>
                <select
                  className="m-input"
                  value={reassignPoId}
                  onChange={(e) => setReassignPoId(e.target.value)}
                  data-testid="reassign-po-select"
                >
                  <option value="">— Unassigned —</option>
                  {reassignPos.map((p) => (
                    <option key={p.po_id} value={p.po_id}>
                      {p.po_number} · {p.customer_name || "—"} · pending {p.pending_qty}
                    </option>
                  ))}
                </select>
                {reassignLoadingPos && (
                  <p style={{ color: "#a8a29e", fontSize: 11, marginTop: 6 }}>Loading POs…</p>
                )}
                <input
                  className="m-input"
                  style={{ marginTop: 8 }}
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  placeholder="Reason (required)"
                  data-testid="reassign-reason-input"
                />
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button
                    className="m-btn"
                    style={{ flex: 1 }}
                    onClick={submitReassign}
                    disabled={busy || !reassignReason.trim()}
                    data-testid="reassign-submit"
                  >
                    {busy ? "Saving…" : "Apply Change"}
                  </button>
                  <button
                    className="m-btn m-btn--ghost"
                    style={{ flex: 1 }}
                    onClick={() => { setReassignOpen(false); setReassignReason(""); }}
                    data-testid="reassign-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Action panel — based on current state */}
            {pallet.state === "PRINTED" && (
              <div className="m-card" style={{ marginTop: 12 }}>
                <p style={{ color: "#a8a29e", fontSize: 13, margin: 0 }}>
                  This pallet hasn't been paired yet. Use <strong>Produce</strong> from the home screen to pair it with a product first.
                </p>
              </div>
            )}

            {pallet.state === "ASSIGNED" && !voidOpen && (
              <>
                <p style={{ color: "#a8a29e", fontSize: 13, margin: "12px 0" }}>
                  Stack {pallet.target_qty} pieces of <strong>{pallet.product_code}</strong>, then tap Produced.
                </p>
                <button
                  className="m-btn"
                  onClick={markProduced}
                  disabled={busy}
                  data-testid="update-mark-produced"
                >
                  <CheckCircle2 className="w-4 h-4 inline mr-1" />
                  {busy ? "Saving…" : `Mark Produced · ${pallet.target_qty} pcs`}
                </button>
                <button
                  className="m-btn m-btn--ghost"
                  style={{ marginTop: 8 }}
                  onClick={() => setVoidOpen(true)}
                  data-testid="update-open-void"
                >
                  <Ban className="w-4 h-4 inline mr-1" /> Void this pallet
                </button>
              </>
            )}

            {pallet.state === "PRODUCED" && !voidOpen && (
              <>
                <button
                  className="m-btn"
                  onClick={markPacked}
                  disabled={busy}
                  data-testid="update-mark-packed"
                >
                  <Package className="w-4 h-4 inline mr-1" />
                  {busy ? "Packing…" : "Pack this pallet"}
                </button>
                <p style={{ color: "#a8a29e", fontSize: 12, margin: "8px 0" }}>
                  Sealing this pallet locks its contents — no more pairing or quantity edits until someone unpacks.
                </p>
                <button
                  className="m-btn m-btn--ghost"
                  style={{ marginTop: 4 }}
                  onClick={downloadCard}
                  data-testid="update-download-card-produced"
                >
                  <FileText className="w-4 h-4 inline mr-1" /> Download A4 card (draft)
                </button>
                <button
                  className="m-btn m-btn--ghost"
                  style={{ marginTop: 8 }}
                  onClick={() => setVoidOpen(true)}
                  data-testid="update-open-void-produced"
                >
                  <Ban className="w-4 h-4 inline mr-1" /> Void this pallet
                </button>
              </>
            )}

            {pallet.state === "PACKED" && !unpackOpen && (
              <>
                <button
                  className="m-btn"
                  onClick={downloadCard}
                  data-testid="update-download-card-packed"
                >
                  <FileText className="w-4 h-4 inline mr-1" /> Download / print A4 card
                </button>
                <p style={{ color: "#a8a29e", fontSize: 12, margin: "8px 0" }}>
                  Sealed on {pallet.packed_at ? fmtDateTime(pallet.packed_at) : "—"} by {pallet.packed_by_email || "—"}.
                </p>
                <button
                  className="m-btn m-btn--ghost"
                  style={{ marginTop: 4 }}
                  onClick={() => setUnpackOpen(true)}
                  data-testid="update-open-unpack"
                >
                  <PackageOpen className="w-4 h-4 inline mr-1" /> Unpack (reopen pallet)
                </button>
              </>
            )}

            {(pallet.state === "IN_CONTAINER" ||
              pallet.state === "DISPATCHED" ||
              pallet.state === "DELIVERED" ||
              pallet.state === "VOIDED") && (
              <div className="m-card" style={{ marginTop: 12 }}>
                <p style={{ color: "#a8a29e", fontSize: 13, margin: 0 }}>
                  No actions available on this pallet from the mobile app
                  {pallet.state === "VOIDED" && pallet.void_reason ? ` · Reason: ${pallet.void_reason}` : ""}.
                </p>
              </div>
            )}

            {voidOpen && (
              <div className="m-card" style={{ marginTop: 12 }} data-testid="void-form">
                <div className="m-card-label" style={{ marginBottom: 8 }}>Reason for void</div>
                <input
                  className="m-input"
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="e.g. wrong product, damaged pallet"
                  autoFocus
                  data-testid="void-reason-input"
                />
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button
                    className="m-btn"
                    style={{ flex: 1, background: "#dc2626" }}
                    onClick={submitVoid}
                    disabled={busy || !voidReason.trim()}
                    data-testid="void-submit"
                  >
                    {busy ? "Voiding…" : "Confirm Void"}
                  </button>
                  <button
                    className="m-btn m-btn--ghost"
                    style={{ flex: 1 }}
                    onClick={() => { setVoidOpen(false); setVoidReason(""); }}
                    data-testid="void-cancel"
                  >
                    Cancel
                  </button>
                </div>
                <p style={{ color: "#a8a29e", fontSize: 11, marginTop: 8 }}>
                  Tip: same operator can void within 5 min. Supervisor (with permission) within 60 min. Post-60-min voids need a reason starting with "OVERRIDE:" and the void permission.
                </p>
              </div>
            )}

            {unpackOpen && (
              <div className="m-card" style={{ marginTop: 12 }} data-testid="unpack-form">
                <div className="m-card-label" style={{ marginBottom: 8 }}>Reason for unpack</div>
                <input
                  className="m-input"
                  value={unpackReason}
                  onChange={(e) => setUnpackReason(e.target.value)}
                  placeholder="e.g. add 2 more pieces, customer changed PO"
                  autoFocus
                  data-testid="unpack-reason-input"
                />
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button
                    className="m-btn"
                    style={{ flex: 1 }}
                    onClick={submitUnpack}
                    disabled={busy || !unpackReason.trim()}
                    data-testid="unpack-submit"
                  >
                    {busy ? "Unpacking…" : "Confirm Unpack"}
                  </button>
                  <button
                    className="m-btn m-btn--ghost"
                    style={{ flex: 1 }}
                    onClick={() => { setUnpackOpen(false); setUnpackReason(""); }}
                    data-testid="unpack-cancel"
                  >
                    Cancel
                  </button>
                </div>
                <p style={{ color: "#a8a29e", fontSize: 11, marginTop: 8 }}>
                  Logged in pallet history. Reprint the A4 card after re-packing.
                </p>
              </div>
            )}

            <button
              className="m-btn m-btn--ghost"
              style={{ marginTop: 16 }}
              onClick={scanAnother}
              data-testid="update-scan-another"
            >
              Scan another pallet
            </button>
          </>
        )}
      </div>
    </>
  );
}



// ============================== My Pallets (recent list, filterable) ==============================

const STATE_FILTERS = [
  { key: "", label: "All" },
  { key: "ASSIGNED", label: "Assigned" },
  { key: "PRODUCED", label: "Produced" },
  { key: "PACKED", label: "Packed" },
  { key: "VOIDED", label: "Voided" },
];

function relTime(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return "just now";
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "—";
  }
}

function MyPallets({ user }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");
  const [scope, setScope] = useState("mine"); // mine | all
  const [perms, setPerms] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const canViewAll = perms.includes("pallet.view");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.permissions();
        setPerms(r?.effective_permissions || []);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const load = async (state, sc) => {
    setLoading(true);
    setErr(null);
    try {
      const data =
        sc === "all"
          ? await api.allPallets({ state: state || undefined, limit: 200 })
          : await api.myRecent({ state: state || undefined, limit: 100 });
      setItems(data || []);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not load pallets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filter, scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, scope]);

  return (
    <>
      <SimpleChrome title={scope === "all" ? "All Pallets" : "My Pallets"} onBack={() => navigate("/mobile")} />
      <div className="m-screen">
        {canViewAll && (
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }} data-testid="my-scope">
            <button
              onClick={() => setScope("mine")}
              className="m-step-pill"
              style={{
                cursor: "pointer",
                flex: 1,
                background: scope === "mine" ? "rgba(234,88,12,0.22)" : "rgba(255,255,255,0.04)",
                color: scope === "mine" ? "#fdba74" : "#a8a29e",
                borderColor: scope === "mine" ? "#ea580c" : "rgba(255,255,255,0.08)",
              }}
              data-testid="scope-mine"
            >
              Mine
            </button>
            <button
              onClick={() => setScope("all")}
              className="m-step-pill"
              style={{
                cursor: "pointer",
                flex: 1,
                background: scope === "all" ? "rgba(234,88,12,0.22)" : "rgba(255,255,255,0.04)",
                color: scope === "all" ? "#fdba74" : "#a8a29e",
                borderColor: scope === "all" ? "#ea580c" : "rgba(255,255,255,0.08)",
              }}
              data-testid="scope-all"
            >
              All operators
            </button>
          </div>
        )}
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }} data-testid="my-filters">
          {STATE_FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setFilter(f.key)}
              className={`m-step-pill`}
              style={{
                cursor: "pointer",
                background: filter === f.key ? "rgba(234,88,12,0.22)" : "rgba(255,255,255,0.04)",
                color: filter === f.key ? "#fdba74" : "#a8a29e",
                borderColor: filter === f.key ? "#ea580c" : "rgba(255,255,255,0.08)",
              }}
              data-testid={`my-filter-${f.key || "all"}`}
            >
              {f.label}
            </button>
          ))}
          <button
            onClick={() => load(filter, scope)}
            className="m-step-pill"
            style={{ cursor: "pointer", marginLeft: "auto" }}
            data-testid="my-refresh"
          >
            ↻
          </button>
        </div>

        {err && <div className="m-error-banner" data-testid="my-error">{err}</div>}

        {loading && (
          <div style={{ textAlign: "center", paddingTop: 30 }}>
            <Loader2 className="animate-spin mx-auto" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="m-card" style={{ textAlign: "center" }}>
            <div style={{ color: "#a8a29e", fontSize: 13 }}>
              No pallets {filter ? `in state ${filter}` : "yet"}
              {scope === "all" ? " across any operator." : ". Go pair some from the Produce screen."}
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="my-list">
            {items.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/mobile/update/${p.id}`)}
                className="m-card"
                style={{ textAlign: "left", cursor: "pointer", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`my-row-${p.id}`}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div className="m-card-mono" style={{ fontSize: 14 }}>{p.id}</div>
                  <StateBadge state={p.state} />
                </div>
                <div className="m-divider" />
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13 }}>
                  <div>
                    <div style={{ color: "#e7e5e4", fontWeight: 600 }}>{p.product_code || "—"}</div>
                    <div style={{ color: "#a8a29e" }}>
                      {p.po_no ? `PO ${p.po_no}` : "Unassigned"}
                      {p.actual_qty != null ? ` · ${p.actual_qty} pcs` : (p.target_qty != null ? ` · target ${p.target_qty}` : "")}
                    </div>
                    {scope === "all" && p.paired_by_email && (
                      <div style={{ color: "#78716c", fontSize: 11, marginTop: 2 }} data-testid={`my-operator-${p.id}`}>
                        by {p.paired_by_email}
                      </div>
                    )}
                  </div>
                  <div style={{ color: "#a8a29e", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
                    <Clock className="w-3 h-3" />
                    {relTime(p.paired_at || p.updated_at)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}


// ============================== Pack flow ==============================

function Pack({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("scan"); // scan | confirm | done
  const [pallet, setPallet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [scanNonce, setScanNonce] = useState(0);
  const [manualMode, setManualMode] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [cardUrl, setCardUrl] = useState(null);
  const packScannerRef = useRef(null);
  const lastRescanTsRef = useRef(0);

  const rescan = () => {
    setErr(null);
    const now = Date.now();
    if (now - lastRescanTsRef.current < 2000) {
      lastRescanTsRef.current = 0;
      setScanNonce((n) => n + 1);
      return;
    }
    lastRescanTsRef.current = now;
    if (packScannerRef.current?.resume) packScannerRef.current.resume();
    else setScanNonce((n) => n + 1);
  };

  const loadPallet = async (id) => {
    const raw = String(id || "").trim().toUpperCase();
    if (!isPalletId(raw)) {
      setErr(`"${raw}" is not a pallet QR. Expected PLT-YYYY-NNNNNN.`);
      packScannerRef.current?.resume?.();
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const p = await api.getPallet(raw);
      if (p.state !== "PRODUCED") {
        setErr(
          `Pallet ${raw} is ${p.state}. Only PRODUCED pallets can be packed` +
          (p.state === "ASSIGNED" ? " — finish production first." : ".")
        );
        packScannerRef.current?.resume?.();
        return;
      }
      setPallet(p);
      setStep("confirm");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Pallet not found");
      packScannerRef.current?.resume?.();
    } finally {
      setBusy(false);
    }
  };

  const confirmPack = async () => {
    if (!pallet) return;
    setBusy(true);
    setErr(null);
    try {
      const p = await api.packPallet(pallet.id, DEVICE_ID);
      setPallet(p);
      // Auto-open A4 card
      try {
        const blob = await api.cardPdfBlob(p.id);
        const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        setCardUrl(url);
        window.open(url, "_blank");
      } catch { /* non-fatal */ }
      toast.success(`${p.id} packed`);
      setStep("done");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not pack pallet");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    if (cardUrl) {
      try { window.URL.revokeObjectURL(cardUrl); } catch { /* ignore */ }
    }
    setStep("scan");
    setPallet(null);
    setErr(null);
    setManualMode(false);
    setManualInput("");
    setCardUrl(null);
  };

  return (
    <>
      <SimpleChrome title="Pack Pallet" onBack={() => navigate("/mobile")} />
      <div className="m-screen">
        {err && <div className="m-error-banner" data-testid="pack-error">{err}</div>}

        {step === "scan" && (
          <>
            <h3 style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 18, margin: "6px 0 10px" }}>
              Scan a PRODUCED pallet
            </h3>
            <p style={{ color: "#a8a29e", fontSize: 13, marginBottom: 10 }}>
              Wrap the pallet tight first, then scan it here. Sealing locks contents so they can't move to another pallet.
            </p>
            {!manualMode ? (
              <Scanner
                ref={packScannerRef}
                key={`pack-${scanNonce}`}
                onDecoded={loadPallet}
                hint="Aim at the pallet sticker (PLT-…)"
              />
            ) : (
              <ManualEntry
                label="Type pallet ID (e.g. PLT-2026-000123)"
                value={manualInput}
                onChange={setManualInput}
                onSubmit={() => loadPallet(manualInput)}
              />
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 8 }}>
              {!manualMode && (
                <button className="m-link-btn" onClick={rescan} data-testid="rescan-pack">↻ Rescan</button>
              )}
              <button
                className="m-link-btn"
                onClick={() => setManualMode((v) => !v)}
                data-testid="toggle-manual-pack"
              >
                <Keyboard className="w-3 h-3 inline mr-1" />
                {manualMode ? "Use camera" : "Type ID instead"}
              </button>
            </div>
          </>
        )}

        {step === "confirm" && pallet && (
          <>
            <div className="m-card" data-testid="pack-confirm-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="m-card-mono" style={{ fontSize: 18 }}>{pallet.id}</div>
                <StateBadge state={pallet.state} />
              </div>
              <div className="m-divider" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div className="m-card-label">Product</div>
                  <div className="m-card-value">{pallet.product_code || "—"}</div>
                </div>
                <div>
                  <div className="m-card-label">Qty</div>
                  <div className="m-card-value">{pallet.actual_qty ?? "—"} pcs</div>
                </div>
                <div>
                  <div className="m-card-label">PO</div>
                  <div className="m-card-mono" style={{ fontSize: 14 }}>{pallet.po_no || "Unassigned"}</div>
                </div>
                <div>
                  <div className="m-card-label">Customer</div>
                  <div className="m-card-value" style={{ fontSize: 13 }}>{pallet.customer_name || "—"}</div>
                </div>
              </div>
            </div>
            <p style={{ color: "#a8a29e", fontSize: 13, margin: "12px 0" }}>
              Confirm to seal this pallet. The A4 card will open automatically — print it and tape it to the wrap.
            </p>
            <button
              className="m-btn"
              onClick={confirmPack}
              disabled={busy}
              data-testid="pack-confirm"
            >
              <Package className="w-4 h-4 inline mr-1" />
              {busy ? "Packing…" : `Pack & print A4 card`}
            </button>
            <button
              className="m-btn m-btn--ghost"
              style={{ marginTop: 8 }}
              onClick={reset}
              data-testid="pack-cancel"
            >
              Cancel — scan another
            </button>
          </>
        )}

        {step === "done" && pallet && (
          <>
            <div className="m-success-banner" data-testid="pack-done-banner">
              <CheckCircle2 className="w-4 h-4 inline mr-1" />
              {pallet.id} packed · {pallet.actual_qty} pcs sealed
            </div>
            <button
              className="m-btn"
              onClick={async () => {
                try {
                  const blob = await api.cardPdfBlob(pallet.id);
                  const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
                  window.open(url, "_blank");
                  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                } catch (ex) {
                  toast.error(ex.response?.data?.detail || "Could not load card");
                }
              }}
              data-testid="pack-reprint-card"
            >
              <FileText className="w-4 h-4 inline mr-1" /> Open / reprint A4 card
            </button>
            <button className="m-btn m-btn--ghost" style={{ marginTop: 8 }} onClick={reset} data-testid="pack-next">
              Pack another pallet
            </button>
            <button className="m-btn m-btn--ghost" style={{ marginTop: 8 }} onClick={() => navigate("/mobile")}>
              Back to home
            </button>
          </>
        )}
      </div>
    </>
  );
}


// ============================== Live Production (read-only) ==============================

function LiveProduction({ user }) {
  const navigate = useNavigate();
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const daysAgoIso = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };

  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [groupBy, setGroupBy] = useState("product");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.liveProduction({ date_from: dateFrom, date_to: dateTo, group_by: groupBy });
      setData(r);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dateFrom, dateTo, groupBy]);

  // Auto-refresh every 60s when looking at Today
  useEffect(() => {
    if (dateFrom !== todayIso() || dateTo !== todayIso()) return;
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, groupBy]);

  const setQuick = (from, to) => { setDateFrom(from); setDateTo(to); };
  const fmtNum = (n) => new Intl.NumberFormat().format(Number(n || 0));
  const isToday = dateFrom === todayIso() && dateTo === todayIso();
  const kpis = data?.kpis || { pallets: 0, pieces: 0, products: 0, operators: 0 };
  const rows = data?.rows || [];

  // Product drill-down: pallets + attached DPR
  const [drill, setDrill] = useState(null); // { row }
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const openDrill = async (row) => {
    setDrill({ row });
    setDrillData(null);
    setDrillLoading(true);
    try {
      const r = await api.liveProductionPallets({
        product_code: row.product_code || "",
        po_no: row.po_number || "",
        date_from: dateFrom,
        date_to: dateTo,
      });
      setDrillData(r);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load pallet details");
    } finally {
      setDrillLoading(false);
    }
  };

  return (
    <>
      <SimpleChrome title="Live Production" onBack={() => navigate("/mobile")} />
      <div className="m-screen" data-testid="mobile-live-production">
        {/* Date quick chips */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          <ChipBtn active={isToday} onClick={() => setQuick(todayIso(), todayIso())} testId="live-today">Today</ChipBtn>
          <ChipBtn active={dateFrom === daysAgoIso(1) && dateTo === daysAgoIso(1)} onClick={() => setQuick(daysAgoIso(1), daysAgoIso(1))} testId="live-yest">Yesterday</ChipBtn>
          <ChipBtn active={dateFrom === daysAgoIso(6) && dateTo === todayIso()} onClick={() => setQuick(daysAgoIso(6), todayIso())} testId="live-7d">7d</ChipBtn>
          <ChipBtn active={dateFrom === daysAgoIso(29) && dateTo === todayIso()} onClick={() => setQuick(daysAgoIso(29), todayIso())} testId="live-30d">30d</ChipBtn>
          <button className="m-back-btn" onClick={fetchData} disabled={loading} title="Refresh" style={{ marginLeft: "auto" }} data-testid="live-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Custom date range pickers */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6,
            marginBottom: 10,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 8,
            padding: 8,
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 11, color: "#94a3b8" }}>
            From
            <input
              type="date"
              value={dateFrom}
              max={dateTo}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.06)",
                color: "#e5e7eb",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 6,
                padding: "6px 8px",
                fontSize: 13,
                colorScheme: "dark",
              }}
              data-testid="live-date-from"
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 11, color: "#94a3b8" }}>
            To
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              max={todayIso()}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.06)",
                color: "#e5e7eb",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 6,
                padding: "6px 8px",
                fontSize: 13,
                colorScheme: "dark",
              }}
              data-testid="live-date-to"
            />
          </label>
        </div>

        {/* KPI strip */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 8,
            marginBottom: 12,
          }}
          data-testid="live-kpis"
        >
          <MobileKpi label="Pallets" value={fmtNum(kpis.pallets)} color="#34d399" testId="live-kpi-pallets" />
          <MobileKpi label="Pieces" value={fmtNum(kpis.pieces)} color="#60a5fa" testId="live-kpi-pieces" />
          <MobileKpi label="Products" value={fmtNum(kpis.products)} color="#fbbf24" testId="live-kpi-products" />
          <MobileKpi label="Operators" value={fmtNum(kpis.operators)} color="#a78bfa" testId="live-kpi-operators" />
        </div>

        {/* Group toggle */}
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <ChipBtn active={groupBy === "product"} onClick={() => setGroupBy("product")} testId="live-group-product">By Product</ChipBtn>
          <ChipBtn active={groupBy === "operator"} onClick={() => setGroupBy("operator")} testId="live-group-operator">By Operator</ChipBtn>
        </div>

        {err && <div style={{ color: "#f87171", fontSize: 13, marginBottom: 8 }}>{err}</div>}

        {/* Row list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="live-rows">
          {rows.length === 0 && !loading && (
            <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "20px 0" }}>
              No production in this range
            </div>
          )}
          {rows.map((r, i) => (
            <div
              key={i}
              data-testid={`live-row-${i}`}
              onClick={groupBy === "product" ? () => openDrill(r) : undefined}
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 10,
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                cursor: groupBy === "product" ? "pointer" : "default",
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                {groupBy === "product" ? (
                  <>
                    <div style={{ fontFamily: "ui-monospace, monospace", fontWeight: 600, fontSize: 14 }}>
                      {r.product_code}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.po_number ? `PO ${r.po_number}` : "Unassigned"}
                      {r.description ? ` · ${r.description}` : ""}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{r.operator}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.products} product(s)</div>
                  </>
                )}
              </div>
              <div style={{ textAlign: "right", display: "flex", alignItems: "center", gap: 6 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{fmtNum(r.pieces)}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.pallets} pal</div>
                </div>
                {groupBy === "product" && <ChevronRight className="w-4 h-4" style={{ color: "#64748b" }} />}
              </div>
            </div>
          ))}
        </div>

        {/* Product drill-down bottom sheet */}
        {drill && (
          <div
            onClick={() => setDrill(null)}
            data-testid="live-drill-sheet"
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 50,
              display: "flex", alignItems: "flex-end",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#0f172a", borderTopLeftRadius: 16, borderTopRightRadius: 16,
                width: "100%", maxHeight: "80vh", overflowY: "auto",
                border: "1px solid rgba(255,255,255,0.08)", padding: 14,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ fontFamily: "ui-monospace, monospace", fontWeight: 700, fontSize: 16, color: "#e5e7eb" }}>
                  {drill.row.product_code}
                </div>
                <button onClick={() => setDrill(null)} className="m-back-btn" data-testid="live-drill-close" style={{ fontSize: 18 }}>×</button>
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10 }}>
                {drill.row.po_number ? `PO ${drill.row.po_number}` : "Unassigned"}
                {drillData ? ` · ${drillData.count} pallets · ${fmtNum(drillData.pieces)} pcs` : ""}
              </div>

              {drillLoading && <div style={{ textAlign: "center", color: "#94a3b8", padding: "20px 0" }}>Loading…</div>}

              {!drillLoading && drillData && drillData.pallets.length === 0 && (
                <div style={{ textAlign: "center", color: "#94a3b8", padding: "20px 0" }}>No pallets found</div>
              )}

              {!drillLoading && drillData && drillData.pallets.map((p) => (
                <div
                  key={p.id}
                  data-testid={`live-drill-row-${p.id}`}
                  style={{
                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)",
                    borderRadius: 10, padding: "8px 10px", marginBottom: 6,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "#e5e7eb" }}>{p.id}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      {p.qty} pcs · {p.paired_by_email || "—"}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {p.report_id ? (
                      <span
                        data-testid={`live-drill-dpr-${p.id}`}
                        style={{ fontSize: 11, color: "#34d399", fontWeight: 600 }}
                        title={`DPR ${p.report_date || ""} · ${p.report_status || ""}`}
                      >
                        DPR {p.report_date || ""}
                      </span>
                    ) : (
                      <span data-testid={`live-drill-nodpr-${p.id}`} style={{ fontSize: 11, color: "#fbbf24" }}>Not on any DPR</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ChipBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        background: active ? "#10b981" : "rgba(255,255,255,0.06)",
        color: active ? "#022c22" : "#cbd5e1",
        border: active ? "1px solid #10b981" : "1px solid rgba(255,255,255,0.1)",
        padding: "6px 10px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function MobileKpi({ label, value, color, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 10,
        padding: "10px 12px",
      }}
    >
      <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
    </div>
  );
}


// ============================== Inventory (view-only) ==============================

const invFmt = (n) => new Intl.NumberFormat().format(Number(n || 0));

// Whole-number money formatters (rate shown with decimals elsewhere; values whole).
const usdFmt = (n) =>
  n == null ? "—" : "$" + new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(Number(n)));
const inrFmt = (n) =>
  n == null ? "—" : "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(Number(n)));

const VALUE_MASK = "••••••";

function invDateOnly(stored) {
  if (!stored) return "—";
  const str = String(stored);
  return str.includes("T") ? str.slice(0, 10) : str.slice(0, 10);
}

const INV_CARD = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 12,
};

/** Short "x ago" label from a millisecond timestamp. */
function agoLabel(ts) {
  if (!ts) return "";
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** A small status pill showing freshness + offline state for cached inventory data. */
function InvFreshness({ ts, offline }) {
  if (!ts && !offline) return null;
  return (
    <div
      data-testid="inv-freshness"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        color: offline ? "#fbbf24" : "#64748b",
        marginBottom: 10,
      }}
    >
      {offline ? <CloudOff className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
      {offline ? `Offline — showing cached data · ${agoLabel(ts)}` : `Updated ${agoLabel(ts)}`}
    </div>
  );
}

/**
 * Touch pull-to-refresh wrapper. Works with the window scroll (the mobile app
 * scrolls the document; `.m-app` has overscroll-behavior:contain so this won't
 * fight the browser's native gesture). Triggers `onRefresh` when pulled past
 * the threshold while already scrolled to the top.
 */
function PullToRefresh({ onRefresh, children }) {
  const THRESHOLD = 70;
  const MAX = 110;
  const startY = useRef(null);
  const [pullY, setPullY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const onTouchStart = (e) => {
    if (window.scrollY <= 0 && !refreshing) {
      startY.current = e.touches[0].clientY;
    } else {
      startY.current = null;
    }
  };

  const onTouchMove = (e) => {
    if (startY.current == null || refreshing) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0 && window.scrollY <= 0) {
      if (!dragging) setDragging(true);
      setPullY(Math.min(MAX, dy * 0.5)); // elastic resistance
    }
  };

  const onTouchEnd = async () => {
    if (startY.current == null) {
      setDragging(false);
      return;
    }
    startY.current = null;
    setDragging(false);
    if (pullY >= THRESHOLD && !refreshing) {
      setRefreshing(true);
      setPullY(THRESHOLD);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPullY(0);
      }
    } else {
      setPullY(0);
    }
  };

  return (
    <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      <div
        data-testid="ptr-indicator"
        style={{
          height: pullY,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          fontSize: 12,
          color: "#94a3b8",
          transition: dragging ? "none" : "height 200ms ease",
        }}
      >
        {(refreshing || pullY > 4) && (
          <>
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              style={{ transform: refreshing ? "none" : `rotate(${pullY * 2}deg)` }}
            />
            {refreshing ? "Refreshing…" : pullY >= THRESHOLD ? "Release to refresh" : "Pull to refresh"}
          </>
        )}
      </div>
      {children}
    </div>
  );
}

function InventoryList({ user }) {
  const navigate = useNavigate();
  const cached = invCache.getCurrent();
  const [data, setData] = useState(cached?.data || null);
  const [cacheTs, setCacheTs] = useState(cached?.ts || null);
  const [loading, setLoading] = useState(!cached);
  const [offline, setOffline] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [valuesRevealed, setValuesRevealed] = useState(false);

  const load = async () => {
    if (!invCache.getCurrent()) setLoading(true);
    setErr(null);
    try {
      const r = await api.inventoryCurrent({ withValue: true });
      setData(r);
      invCache.setCurrent(r);
      setCacheTs(Date.now());
      setOffline(false);
    } catch (e) {
      const fallback = invCache.getCurrent();
      if (fallback?.data) {
        setData(fallback.data);
        setCacheTs(fallback.ts);
        setOffline(true);
      } else {
        setErr(e.response?.data?.detail || "Failed to load inventory");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const items = data?.items || [];
  const totals = data?.totals || {};
  const hasCutover = !!data?.cutover_date;
  const s = search.trim().toLowerCase();
  const filtered = items.filter(
    (r) =>
      !s ||
      (r.product_code || "").toLowerCase().includes(s) ||
      (r.description || "").toLowerCase().includes(s),
  );

  return (
    <>
      <SimpleChrome title="Live Inventory" onBack={() => navigate("/mobile")} />
      <PullToRefresh onRefresh={load}>
      <div className="m-screen" data-testid="mobile-inventory-list">
        <InvFreshness ts={cacheTs} offline={offline} />
        {!loading && !hasCutover && (
          <div
            data-testid="inv-no-cutover-banner"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(251,191,36,0.1)",
              border: "1px solid rgba(251,191,36,0.2)",
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 12,
              color: "#fbbf24",
              fontSize: 13,
            }}
          >
            <AlertTriangle className="w-4 h-4" style={{ flexShrink: 0 }} />
            Estimates only — no active cutover set
          </div>
        )}

        {/* Hero KPI */}
        <div
          data-testid="inv-kpi-live"
          style={{ ...INV_CARD, padding: 16, textAlign: "center", marginBottom: 12 }}
        >
          <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8 }}>
            Live Inventory On Hand
          </div>
          <div style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 40, color: "#10b981", lineHeight: 1.1, marginTop: 4 }}>
            {loading ? "—" : invFmt(totals.live_pieces)}
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>pieces</div>
          <div data-testid="inv-hero-weight" style={{ marginTop: 8, display: "inline-flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 20, color: "#a78bfa" }}>
              {loading ? "—" : `${invFmt(totals.total_weight_kg)} kg`}
            </span>
            <span style={{ fontSize: 10, color: "#64748b" }}>
              net weight{totals.unweighed_count ? ` · ${totals.unweighed_count} no wt` : ""}
            </span>
          </div>
          {data?.valued && (
            <div data-testid="inv-hero-value" style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <button
                onClick={(e) => { e.stopPropagation(); setValuesRevealed((v) => !v); }}
                data-testid="inv-value-reveal-btn"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6, margin: "0 auto",
                  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
                  color: "#cbd5e1", borderRadius: 999, padding: "5px 12px", fontSize: 11, cursor: "pointer",
                }}
              >
                {valuesRevealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {valuesRevealed ? "Hide value" : "Show value"}
              </button>
              {valuesRevealed && (
                <>
                  <div style={{ display: "flex", justifyContent: "center", gap: 14, alignItems: "baseline", marginTop: 8 }}>
                    <span style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 20, color: "#f1f5f9" }}>{usdFmt(totals.value_usd)}</span>
                    <span style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 20, color: "#a3e635" }}>{inrFmt(totals.value_inr)}</span>
                  </div>
                  <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                    Inventory value · ₹{Number(data.fx?.rate || 0).toFixed(2)}/$1
                    {data.fx?.is_override ? " (manual)" : ""}
                    {data.fx?.is_stale ? " · stale" : ""}
                    {totals.unpriced_count ? ` · ${totals.unpriced_count} unpriced` : ""}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* 2x2 secondary KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }}>
          <MobileKpi label="Opening" value={invFmt(totals.opening_pieces)} color="#60a5fa" testId="inv-kpi-opening" />
          <MobileKpi label="Produced (+)" value={invFmt(totals.produced_pieces)} color="#34d399" testId="inv-kpi-produced" />
          <MobileKpi label="Dispatched (−)" value={invFmt(totals.dispatched_pieces)} color="#fbbf24" testId="inv-kpi-dispatched" />
          <MobileKpi label="Adjustments (±)" value={invFmt(totals.adjustment_pieces)} color="#a78bfa" testId="inv-kpi-adjustments" />
        </div>

        {/* Search */}
        <div style={{ position: "relative", marginBottom: 10 }}>
          <Search className="w-4 h-4" style={{ position: "absolute", left: 12, top: 13, color: "#64748b" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code or description…"
            data-testid="inv-search-input"
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.04)",
              color: "#e5e7eb",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 10,
              padding: "11px 12px 11px 36px",
              fontSize: 14,
              outline: "none",
            }}
          />
        </div>

        {err && <div style={{ color: "#f87171", fontSize: 13, marginBottom: 8 }}>{err}</div>}

        {loading ? (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <Loader2 className="w-6 h-6 animate-spin" style={{ margin: "0 auto", color: "#10b981" }} />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", paddingBottom: 24 }} data-testid="inv-rows">
            {filtered.length === 0 ? (
              <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "24px 0" }}>
                {items.length === 0 ? "No inventory rows yet" : "No products match your search"}
              </div>
            ) : (
              filtered.map((r, i) => {
                const unresolved = r.catalog_resolved === false;
                return (
                  <button
                    key={`${r.product_code}-${i}`}
                    onClick={() =>
                      navigate(`/mobile/inventory/product/${encodeURIComponent(r.product_code)}`, {
                        state: { description: r.description },
                      })
                    }
                    data-testid={`inv-row-${i}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: "12px 4px",
                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                      background: "transparent",
                      textAlign: "left",
                      width: "100%",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 600, fontSize: 14, color: "#f1f5f9" }}>
                          {r.product_code}
                        </span>
                        {unresolved && (
                          <AlertCircle className="w-3.5 h-3.5" style={{ color: "#f87171", flexShrink: 0 }} title="Unmapped — fix on desktop" />
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.description || "—"}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 17, color: "#f1f5f9", lineHeight: 1.1 }}>
                        {invFmt(r.live_pieces)}
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                        <span style={{ color: "#34d399" }}>+{invFmt(r.produced_pieces)}</span>
                        {" / "}
                        <span style={{ color: "#fbbf24" }}>−{invFmt(r.dispatched_pieces)}</span>
                      </div>
                      {r.weighed && (
                        <div data-testid={`inv-row-weight-${i}`} style={{ fontSize: 11, color: "#a78bfa", marginTop: 2, fontFamily: "Chivo", fontWeight: 600 }}>
                          {invFmt(r.total_weight_kg)} kg
                        </div>
                      )}
                      {data?.valued && r.priced && valuesRevealed && (
                        <div data-testid={`inv-row-value-${i}`} style={{ fontSize: 11, color: "#cbd5e1", marginTop: 2, fontFamily: "Chivo", fontWeight: 600 }}>
                          {usdFmt(r.value_usd)} · {inrFmt(r.value_inr)}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
      </PullToRefresh>
    </>
  );
}

function InvBreakdownRow({ leftTop, leftBottom, rightTop, rightColor, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "11px 4px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 13, color: "#e5e7eb" }}>{leftTop}</div>
        <div style={{ fontSize: 11, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {leftBottom}
        </div>
      </div>
      <div style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 15, color: rightColor, flexShrink: 0 }}>{rightTop}</div>
    </div>
  );
}

function InvSectionHeader({ icon, label, total, color }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 14,
        marginBottom: 4,
      }}
    >
      <span style={{ color, display: "inline-flex" }}>{icon}</span>
      <span style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 13, color: "#e5e7eb" }}>{label}</span>
      <span style={{ marginLeft: "auto", fontFamily: "Chivo", fontWeight: 700, fontSize: 13, color }}>{total}</span>
    </div>
  );
}

function ProductDetail({ user }) {
  const navigate = useNavigate();
  const { code } = useParams();
  const location = useLocation();
  const description = location.state?.description || "";
  const cached = invCache.getBreakdown(code);
  const [data, setData] = useState(cached?.data || null);
  const [cacheTs, setCacheTs] = useState(cached?.ts || null);
  const [loading, setLoading] = useState(!cached);
  const [offline, setOffline] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all"); // all | produced | dispatched | opening | adjustments

  const load = async () => {
    if (!invCache.getBreakdown(code)) setLoading(true);
    setErr(null);
    try {
      const r = await api.inventoryBreakdown(code);
      setData(r);
      invCache.setBreakdown(code, r);
      setCacheTs(Date.now());
      setOffline(false);
    } catch (e) {
      const fallback = invCache.getBreakdown(code);
      if (fallback?.data) {
        setData(fallback.data);
        setCacheTs(fallback.ts);
        setOffline(true);
      } else {
        setErr(e.response?.data?.detail || "Failed to load breakdown");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const totals = data?.totals || {};
  const produced = data?.produced || [];
  const dispatched = data?.dispatched || [];
  const opening = data?.opening || [];
  const adjustments = data?.adjustments || [];

  const show = (k) => filter === "all" || filter === k;

  return (
    <>
      <SimpleChrome title={code} onBack={() => navigate("/mobile/inventory")} />
      <PullToRefresh onRefresh={load}>
      <div className="m-screen" data-testid="mobile-inventory-detail">
        <InvFreshness ts={cacheTs} offline={offline} />
        {/* Header card */}
        <div style={{ ...INV_CARD, padding: 16, marginBottom: 12 }}>
          {description && (
            <div style={{ fontSize: 13, color: "#cbd5e1", marginBottom: 12 }}>{description}</div>
          )}
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
            <span style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8 }}>
              Live On Hand
            </span>
            <span style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 34, color: "#10b981", lineHeight: 1 }}>
              {loading ? "—" : invFmt(totals.live_pieces)}
            </span>
          </div>
          {data?.total_weight_kg != null && (
            <div data-testid="inv-detail-weight" style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <span style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8 }}>
                Net Weight{data?.net_weight_per_piece != null ? ` · ${data.net_weight_per_piece} kg/pc` : ""}
              </span>
              <span style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 22, color: "#a78bfa", lineHeight: 1 }}>
                {invFmt(data.total_weight_kg)} kg
              </span>
            </div>
          )}
        </div>

        {/* Filter chips */}
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8, marginBottom: 4 }}>
          <ChipBtn active={filter === "all"} onClick={() => setFilter("all")} testId="inv-filter-all">All</ChipBtn>
          <ChipBtn active={filter === "produced"} onClick={() => setFilter("produced")} testId="inv-filter-produced">Produced</ChipBtn>
          <ChipBtn active={filter === "dispatched"} onClick={() => setFilter("dispatched")} testId="inv-filter-dispatched">Dispatched</ChipBtn>
          <ChipBtn active={filter === "opening"} onClick={() => setFilter("opening")} testId="inv-filter-opening">Opening</ChipBtn>
          <ChipBtn active={filter === "adjustments"} onClick={() => setFilter("adjustments")} testId="inv-filter-adjustments">Adjustments</ChipBtn>
        </div>

        {err && <div style={{ color: "#f87171", fontSize: 13, marginBottom: 8 }}>{err}</div>}

        {loading ? (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <Loader2 className="w-6 h-6 animate-spin" style={{ margin: "0 auto", color: "#10b981" }} />
          </div>
        ) : (
          <div style={{ paddingBottom: 24 }} data-testid="inv-breakdown">
            {/* Produced */}
            {show("produced") && (
              <>
                <InvSectionHeader
                  icon={<ArrowUpCircle className="w-4 h-4" />}
                  label="Produced (pallet scans)"
                  total={`+${invFmt(totals.produced_pieces)}`}
                  color="#34d399"
                />
                {produced.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#64748b", padding: "8px 4px" }}>No production scans.</div>
                ) : (
                  produced.map((p, i) => (
                    <InvBreakdownRow
                      key={`pr-${i}`}
                      testId={`inv-bd-produced-${i}`}
                      leftTop={p.pallet_id}
                      leftBottom={`${invDateOnly(p.produced_at)}${p.report?.report_date ? ` · DPR ${p.report.report_date}` : p.report?.report_no ? ` · DPR ${p.report.report_no}` : ""}`}
                      rightTop={`+${invFmt(p.qty_pieces)}`}
                      rightColor="#34d399"
                    />
                  ))
                )}
              </>
            )}

            {/* Dispatched */}
            {show("dispatched") && (
              <>
                <InvSectionHeader
                  icon={<ArrowDownCircle className="w-4 h-4" />}
                  label="Dispatched (packing lists)"
                  total={`−${invFmt(totals.dispatched_pieces)}`}
                  color="#fbbf24"
                />
                {dispatched.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#64748b", padding: "8px 4px" }}>No dispatches.</div>
                ) : (
                  dispatched.map((d, i) => (
                    <InvBreakdownRow
                      key={`dp-${i}`}
                      testId={`inv-bd-dispatched-${i}`}
                      leftTop={d.invoice_no || "—"}
                      leftBottom={`${d.file_name ? `${d.file_name} · ` : ""}${invDateOnly(d.dispatch_date)}`}
                      rightTop={`−${invFmt(d.qty_pieces)}`}
                      rightColor="#fbbf24"
                    />
                  ))
                )}
              </>
            )}

            {/* Opening */}
            {show("opening") && (
              <>
                <InvSectionHeader
                  icon={<Package className="w-4 h-4" />}
                  label="Opening (cutover baseline)"
                  total={invFmt(totals.opening_pieces)}
                  color="#60a5fa"
                />
                {opening.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#64748b", padding: "8px 4px" }}>No opening baseline.</div>
                ) : (
                  opening.map((o, i) => (
                    <InvBreakdownRow
                      key={`op-${i}`}
                      testId={`inv-bd-opening-${i}`}
                      leftTop={o.po_number || "—"}
                      leftBottom={o.customer_name || ""}
                      rightTop={invFmt(o.qty_pieces)}
                      rightColor="#60a5fa"
                    />
                  ))
                )}
              </>
            )}

            {/* Adjustments */}
            {show("adjustments") && (
              <>
                <InvSectionHeader
                  icon={<Sliders className="w-4 h-4" />}
                  label="Manual Adjustments"
                  total={`${totals.adjustment_pieces > 0 ? "+" : ""}${invFmt(totals.adjustment_pieces)}`}
                  color="#a78bfa"
                />
                {adjustments.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#64748b", padding: "8px 4px" }}>No adjustments.</div>
                ) : (
                  adjustments.map((a, i) => (
                    <InvBreakdownRow
                      key={`adj-${i}`}
                      testId={`inv-bd-adjustment-${i}`}
                      leftTop={a.reason || "Manual Adjustment"}
                      leftBottom={invDateOnly(a.effective_date)}
                      rightTop={`${a.qty_delta > 0 ? "+" : ""}${invFmt(a.qty_delta)}`}
                      rightColor="#a78bfa"
                    />
                  ))
                )}
              </>
            )}
          </div>
        )}
      </div>
      </PullToRefresh>
    </>
  );
}
